import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { AgencyProfileCard } from '../../components/agent/AgencyProfileEditor';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import PropertyChat from '../../components/PropertyChat';
import TwoFactorSetup from '../../components/TwoFactorSetup';
import {
  Home, Building, Plus, FileText, Users, Bell, User, LogOut, Menu,
  MapPin, Upload, CheckCircle, Clock, AlertCircle, X, Check, ChevronRight, ChevronLeft,
  Camera, File, Trash2, Eye, Send, PenTool, Loader2, Activity, Settings, Edit,
  Scale, Table2, Award, Star, Phone, Mail, Download, Briefcase, Gavel, TrendingUp,
  MessageSquare, Shield, ArrowLeft
} from 'lucide-react';
import { 
  DOCUMENT_CONFIGS, 
  validateFile, 
  uploadToCloud, 
  validatePropertyField,
  calculateCompletion 
} from '../../utils/documentUpload';

// ============ SELLER FORM CONTEXT ============
// This context persists form data across navigation
const SellerFormContext = createContext(null);

const initialSellerFormData = {
  address: '',
  city: '',
  province: '',
  postal_code: '',
  property_type: 'appartamento',
  sqm: '',
  rooms: '',
  floor: '',
  description: ''
};

export const SellerFormProvider = ({ children }) => {
  const [formData, setFormData] = useState(() => {
    const saved = sessionStorage.getItem('sellerPropertyFormData');
    return saved ? JSON.parse(saved) : initialSellerFormData;
  });
  
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = sessionStorage.getItem('sellerPropertyFormStep');
    return saved ? parseInt(saved) : 1;
  });
  
  const [propertyId, setPropertyId] = useState(() => {
    const saved = sessionStorage.getItem('sellerPropertyId');
    return saved || null;
  });

  useEffect(() => {
    sessionStorage.setItem('sellerPropertyFormData', JSON.stringify(formData));
  }, [formData]);
  
  useEffect(() => {
    sessionStorage.setItem('sellerPropertyFormStep', currentStep.toString());
  }, [currentStep]);
  
  useEffect(() => {
    if (propertyId) {
      sessionStorage.setItem('sellerPropertyId', propertyId);
    }
  }, [propertyId]);

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const resetForm = () => {
    setFormData(initialSellerFormData);
    setCurrentStep(1);
    setPropertyId(null);
    sessionStorage.removeItem('sellerPropertyFormData');
    sessionStorage.removeItem('sellerPropertyFormStep');
    sessionStorage.removeItem('sellerPropertyId');
  };

  return (
    <SellerFormContext.Provider value={{
      formData,
      setFormData,
      updateFormData,
      currentStep,
      setCurrentStep,
      propertyId,
      setPropertyId,
      resetForm
    }}>
      {children}
    </SellerFormContext.Provider>
  );
};

export const useSellerForm = () => {
  const context = useContext(SellerFormContext);
  if (!context) {
    return {
      formData: initialSellerFormData,
      setFormData: () => {},
      updateFormData: () => {},
      currentStep: 1,
      setCurrentStep: () => {},
      propertyId: null,
      setPropertyId: () => {},
      resetForm: () => {}
    };
  }
  return context;
};

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get('/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const menuItems = [
    { path: '/seller', icon: Home, label: 'Dashboard', exact: true },
    { path: '/seller/immobili', icon: Building, label: 'I Miei Immobili' },
    { path: '/seller/carica', icon: Plus, label: 'Carica Immobile' },
    { path: '/seller/gare', icon: TrendingUp, label: 'Gare di Vendita' },
    { path: '/seller/proposte', icon: Users, label: 'Proposte Agenti' },
    { path: '/seller/mandati', icon: FileText, label: 'Mandati Firmati' },
    { path: '/seller/messaggi', icon: MessageSquare, label: 'Messaggi' },
    { path: '/seller/notifiche', icon: Bell, label: 'Notifiche', badge: unreadCount },
    { path: '/seller/profilo', icon: User, label: 'Profilo' },
    { path: '/seller/sicurezza', icon: Shield, label: 'Sicurezza' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 h-full w-72 bg-[#F5F5F7] z-50 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6">
            <Link to="/" className="flex items-center">
              <img 
                src="/weqasa-logo.png" 
                alt="WEQASA" 
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>

          {/* User Info */}
          <div className="px-6 pb-6">
            <div className="flex items-center space-x-3 p-4 bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
              <div className="w-10 h-10 bg-[#0071e3]/10 rounded-xl flex items-center justify-center">
                <Home className="h-5 w-5 text-[#0071e3]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-[#1D1D1F] truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <span className="text-[12px] text-[#86868B]">Proprietario</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.path, item.exact)
                    ? 'bg-white text-[#1D1D1F] shadow-[0_2px_8px_rgb(0,0,0,0.04)]'
                    : 'text-[#86868B] hover:bg-white/50 hover:text-[#1D1D1F]'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className="h-5 w-5" strokeWidth={1.5} />
                  <span className="text-[14px] font-medium">{item.label}</span>
                </div>
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 w-full text-[#86868B] hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
              <span className="text-[14px] font-medium">Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Overview
const SellerOverview = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propsRes, proposalsRes, notifsRes] = await Promise.all([
        axios.get('/seller/properties'),
        axios.get('/seller/proposals'),
        axios.get('/notifications')
      ]);
      setProperties(propsRes.data);
      setProposals(proposalsRes.data.filter(p => p.status === 'pending'));
      setNotifications(notifsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { label: 'Bozza', class: 'badge-pending' },
      'pending_review': { label: 'In Revisione', class: 'badge-auction' },
      'approved': { label: 'Approvato', class: 'badge-active' },
      'proposed_to_agents': { label: 'Proposto ad Agenti', class: 'badge-auction' },
      'mandate_signed': { label: 'Mandato Firmato', class: 'badge-active' },
      'rejected': { label: 'Rifiutato', class: 'badge-closed' }
    };
    const s = statusMap[status] || { label: status, class: 'badge-pending' };
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="seller-dashboard">
      <div className="bg-gradient-to-r from-[#0071e3] to-[#005BB5] rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold  mb-2">
          Benvenuto, {user?.first_name}!
        </h1>
        <p className="text-white/90">
          Gestisci i tuoi immobili e le proposte degli agenti
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
          <Building className="h-8 w-8 text-[#0071e3] mb-3" />
          <div className="text-2xl font-semibold text-[#1D1D1F]">{properties.length}</div>
          <div className="text-sm text-[#86868B]">I Miei Immobili</div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
          <Clock className="h-8 w-8 text-[#0071e3] mb-3" />
          <div className="text-2xl font-semibold text-[#1D1D1F]">
            {properties.filter(p => p.status === 'pending_review').length}
          </div>
          <div className="text-sm text-[#86868B]">In Revisione</div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
          <Users className="h-8 w-8 text-blue-500 mb-3" />
          <div className="text-2xl font-semibold text-[#1D1D1F]">{proposals.length}</div>
          <div className="text-sm text-[#86868B]">Proposte in Attesa</div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
          <CheckCircle className="h-8 w-8 text-green-500 mb-3" />
          <div className="text-2xl font-semibold text-[#1D1D1F]">
            {properties.filter(p => p.status === 'mandate_signed').length}
          </div>
          <div className="text-sm text-[#86868B]">Mandati Firmati</div>
        </div>
      </div>

      {/* Pending Proposals Alert */}
      {proposals.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">
                  Hai {proposals.length} proposta/e in attesa di risposta
                </p>
                <p className="text-sm text-blue-700">Valuta le proposte degli agenti per i tuoi immobili</p>
              </div>
            </div>
            <Link to="/seller/proposte">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Vedi Proposte
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* My Properties */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-[#1D1D1F]">I Miei Immobili</h2>
          <Link to="/seller/carica">
            <Button size="sm" className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Carica Immobile
            </Button>
          </Link>
        </div>
        {properties.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {properties.slice(0, 5).map((prop) => (
              <div key={prop.property_id} className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Building className="h-6 w-6 text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1D1D1F]">
                      {prop.address}, {prop.city}
                    </p>
                    <p className="text-sm text-[#86868B]">
                      Completezza documenti: {prop.document_completion_percentage || 0}%
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusBadge(prop.status)}
                  <Link to={`/seller/immobili/${prop.property_id}`}>
                    <Button size="sm" variant="outline">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Building className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#86868B]">Non hai ancora caricato immobili</p>
            <Link to="/seller/carica" className="text-[#0071e3] hover:underline mt-2 inline-block">
              Carica il tuo primo immobile
            </Link>
          </div>
        )}
      </div>

      {/* Recent Notifications */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-[#1D1D1F]">Notifiche Recenti</h2>
          <Link to="/seller/notifiche" className="text-[#0071e3] text-sm hover:underline">
            Vedi tutte
          </Link>
        </div>
        {notifications.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {notifications.map((notif) => (
              <div key={notif.notification_id} className={`p-4 ${!notif.read ? 'bg-blue-50/50' : ''}`}>
                <div className="flex items-start space-x-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${!notif.read ? 'bg-blue-500' : 'bg-gray-300'}`} />
                  <div>
                    <p className="font-medium text-[#1D1D1F]">{notif.title}</p>
                    <p className="text-sm text-gray-600">{notif.message}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {notif.created_at ? format(new Date(notif.created_at), 'dd/MM/yyyy HH:mm', { locale: it }) : ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-[#86868B]">
            <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p>Nessuna notifica</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Upload Property Page
const UploadPropertyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { 
    formData, 
    updateFormData, 
    currentStep, 
    setCurrentStep, 
    propertyId, 
    setPropertyId, 
    resetForm 
  } = useSellerForm();

  const handleChange = (e) => {
    updateFormData({ [e.target.name]: e.target.value });
  };

  const handleSubmitProperty = async () => {
    if (!formData.address || !formData.city || !formData.province || !formData.postal_code) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/seller/properties', {
        ...formData,
        sqm: formData.sqm ? parseFloat(formData.sqm) : null,
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        floor: formData.floor ? parseInt(formData.floor) : null
      });
      setPropertyId(response.data.property_id);
      toast.success('Immobile creato! Ora carica i documenti.');
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentsComplete = () => {
    // After documents, go to agent selection
    setCurrentStep(3);
  };

  const handleFinish = () => {
    const pid = propertyId;
    resetForm();
    navigate(`/seller/immobili/${pid}`);
  };

  const handleGoBack = () => {
    setCurrentStep(currentStep - 1);
  };

  // Step indicator component with 3 steps
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[
        { num: 1, label: 'Dati' },
        { num: 2, label: 'Documenti' },
        { num: 3, label: 'Agenti' }
      ].map((s, index) => (
        <React.Fragment key={s.num}>
          <button
            onClick={() => {
              if (s.num === 1 || (s.num === 2 && propertyId) || (s.num === 3 && propertyId)) {
                setCurrentStep(s.num);
              }
            }}
            disabled={s.num > 1 && !propertyId}
            className={`flex flex-col items-center transition-all ${
              s.num > 1 && !propertyId ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium mb-1 ${
              currentStep >= s.num ? 'bg-[#0071e3] text-white' : 'bg-gray-200 text-[#86868B]'
            }`}>
              {currentStep > s.num ? <CheckCircle className="h-5 w-5" /> : s.num}
            </div>
            <span className="text-xs text-gray-500">{s.label}</span>
          </button>
          {index < 2 && (
            <div className={`w-16 h-1 mx-2 ${currentStep > s.num ? 'bg-[#0071e3]' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/seller/immobili')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-semibold text-[#1D1D1F]">Carica Nuovo Immobile</h1>
        </div>
        <div className="text-sm text-gray-500">
          Step {currentStep} di 3
        </div>
      </div>

      {/* Progress Steps */}
      <StepIndicator />

      {/* Step 1: Property Details */}
      {currentStep === 1 && (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
          <h2 className="text-lg font-semibold text-[#1D1D1F] mb-6">Dati Immobile</h2>
          
          <div className="space-y-6">
            <div>
              <Label htmlFor="address">Indirizzo *</Label>
              <Input
                id="address"
                name="address"
                placeholder="Via Roma 123"
                value={formData.address}
                onChange={handleChange}
                data-testid="property-address"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">Città *</Label>
                <Input
                  id="city"
                  name="city"
                  placeholder="Roma"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="property-city"
                />
              </div>
              <div>
                <Label htmlFor="province">Provincia *</Label>
                <Input
                  id="province"
                  name="province"
                  placeholder="RM"
                  value={formData.province}
                  onChange={handleChange}
                  maxLength={2}
                />
              </div>
              <div>
                <Label htmlFor="postal_code">CAP *</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  placeholder="00100"
                  value={formData.postal_code}
                  onChange={handleChange}
                  maxLength={5}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="property_type">Tipologia</Label>
                <Select value={formData.property_type} onValueChange={(v) => updateFormData({ property_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="appartamento">Appartamento</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="casa">Casa Indipendente</SelectItem>
                    <SelectItem value="attico">Attico</SelectItem>
                    <SelectItem value="ufficio">Ufficio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sqm">Superficie (m²)</Label>
                <Input
                  id="sqm"
                  name="sqm"
                  type="number"
                  placeholder="100"
                  value={formData.sqm}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="rooms">Locali</Label>
                <Input
                  id="rooms"
                  name="rooms"
                  type="number"
                  placeholder="4"
                  value={formData.rooms}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="floor">Piano</Label>
                <Input
                  id="floor"
                  name="floor"
                  type="number"
                  placeholder="2"
                  value={formData.floor}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Descrizione (opzionale)</Label>
              <Textarea
                id="description"
                name="description"
                placeholder="Descrivi brevemente l'immobile..."
                rows={4}
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => navigate('/seller/immobili')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button 
                onClick={handleSubmitProperty} 
                disabled={loading}
                className="btn-primary"
                data-testid="submit-property-btn"
              >
                {loading ? 'Creazione...' : 'Continua con i Documenti'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Documents */}
      {currentStep === 2 && propertyId && (
        <div className="space-y-4">
          <div className="flex justify-start mb-4">
            <Button variant="outline" onClick={handleGoBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai Dati Immobile
            </Button>
          </div>
          <DocumentUploadSection propertyId={propertyId} onComplete={handleDocumentsComplete} />
        </div>
      )}

      {/* Step 3: Agent Selection */}
      {currentStep === 3 && propertyId && (
        <div className="space-y-4">
          <div className="flex justify-start mb-4">
            <Button variant="outline" onClick={handleGoBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai Documenti
            </Button>
          </div>
          <AgentSelectionSection propertyId={propertyId} onComplete={handleFinish} />
        </div>
      )}
    </div>
  );
};

// Agent Selection Section - Seller chooses which agents to propose their property to
const AgentSelectionSection = ({ propertyId, onComplete }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/seller/available-agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Errore nel caricamento degli agenti');
    } finally {
      setLoading(false);
    }
  };

  const toggleAgent = (agentId) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const handleSubmit = async () => {
    if (selectedAgents.length === 0) {
      toast.error('Seleziona almeno un agente');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/seller/properties/${propertyId}/propose-to-agents`, {
        agent_ids: selectedAgents
      });
      toast.success(`Immobile proposto a ${selectedAgents.length} agente/i!`);
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio delle proposte');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
            <Users className="h-5 w-5 text-[#0071e3]" />
            Seleziona gli Agenti
          </h2>
          <p className="text-[#86868B] mt-1">
            Scegli a quali agenti proporre il tuo immobile. WEQASA ti consiglia i migliori professionisti per rapidità, efficienza e affidabilità.
          </p>
        </div>

        {/* Quality Badges Legend */}
        <div className="flex flex-wrap gap-3 mb-6 pb-4 border-b">
          <div className="flex items-center gap-1 text-sm">
            <Award className="h-4 w-4 text-amber-500" />
            <span className="text-gray-600">Certificato WEQASA</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <Star className="h-4 w-4 text-yellow-500" />
            <span className="text-gray-600">Rating</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <Clock className="h-4 w-4 text-blue-500" />
            <span className="text-gray-600">Tempo risposta</span>
          </div>
        </div>

        {/* Agent Grid */}
        {agents.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((agent) => (
              <div
                key={agent.user_id}
                onClick={() => toggleAgent(agent.user_id)}
                className={`border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedAgents.includes(agent.user_id)
                    ? 'border-[#0071e3] bg-[#0071e3]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Agent Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold ${
                      agent.weqasa_certified ? 'bg-gradient-to-br from-[#0071e3] to-purple-600' : 'bg-gray-400'
                    }`}>
                      {agent.agency_name?.[0] || agent.first_name?.[0] || 'A'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1D1D1F] text-sm">
                        {agent.agency_name || `${agent.first_name} ${agent.last_name}`}
                      </h3>
                      {agent.city && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {agent.city}
                        </p>
                      )}
                    </div>
                  </div>
                  {selectedAgents.includes(agent.user_id) && (
                    <CheckCircle className="h-6 w-6 text-[#0071e3]" />
                  )}
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {agent.weqasa_certified && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs">
                      <Award className="h-3 w-3 mr-1" />
                      Certificato
                    </Badge>
                  )}
                  {agent.rating >= 4.5 && (
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      Top Rated
                    </Badge>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="flex items-center justify-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500" />
                      <span className="font-bold text-sm">{agent.rating?.toFixed(1) || '4.5'}</span>
                    </div>
                    <span className="text-[10px] text-gray-500">Rating</span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <span className="font-bold text-sm">{agent.completed_sales || 0}</span>
                    <span className="text-[10px] text-gray-500 block">Vendite</span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <span className="font-bold text-sm">{Math.round(agent.avg_response_time || 24)}h</span>
                    <span className="text-[10px] text-gray-500 block">Risposta</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessun agente disponibile al momento</p>
          </div>
        )}

        {/* Selected Count & Submit */}
        <div className="mt-6 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-gray-600">
                {selectedAgents.length} agente/i selezionato/i
              </span>
              {selectedAgents.length > 0 && (
                <button 
                  onClick={() => setSelectedAgents([])}
                  className="ml-3 text-sm text-red-500 hover:text-red-700"
                >
                  Deseleziona tutti
                </button>
              )}
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting || selectedAgents.length === 0}
              className="btn-primary"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Proponi agli Agenti Selezionati
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Come funziona?</p>
            <p className="text-sm text-blue-700 mt-1">
              Gli agenti selezionati riceveranno una notifica e valuteranno il tuo immobile. 
              Riceverai le loro proposte con la valutazione di mercato e il prezzo consigliato per la Gara di Vendita.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Document Upload Section Component with Advanced Validation
const DocumentUploadSection = ({ propertyId, onComplete }) => {
  const [completion, setCompletion] = useState({ percentage: 0, missing: [], uploaded: 0, total: 0 });
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});

  const documentTypes = [
    { key: 'identity_document', ...DOCUMENT_CONFIGS.identity_document },
    { key: 'photos', ...DOCUMENT_CONFIGS.photos },
    { key: 'planimetria_catastale', ...DOCUMENT_CONFIGS.planimetria_catastale },
    { key: 'visura_catastale', ...DOCUMENT_CONFIGS.visura_catastale },
    { key: 'atto_provenienza', ...DOCUMENT_CONFIGS.atto_provenienza },
    { key: 'certificato_ape', ...DOCUMENT_CONFIGS.certificato_ape },
    { key: 'urbanistica', ...DOCUMENT_CONFIGS.urbanistica }
  ];

  useEffect(() => {
    const newCompletion = calculateCompletion(documents);
    setCompletion(newCompletion);
  }, [documents]);

  const handleFileSelect = async (docType, files) => {
    const fileArray = Array.from(files);
    const config = DOCUMENT_CONFIGS[docType];
    
    // Clear previous errors
    setErrors(prev => ({ ...prev, [docType]: null }));
    
    // Validate each file
    for (const file of fileArray) {
      const validation = await validateFile(file, docType);
      if (!validation.valid) {
        setErrors(prev => ({ ...prev, [docType]: validation.error }));
        toast.error(validation.error);
        return;
      }
    }

    // Check minimum count for photos
    if (docType === 'photos' && fileArray.length < (config.minCount || 1)) {
      const error = `Carica almeno ${config.minCount} foto`;
      setErrors(prev => ({ ...prev, [docType]: error }));
      toast.error(error);
      return;
    }

    // Start upload
    setUploading(prev => ({ ...prev, [docType]: true }));
    setUploadProgress(prev => ({ ...prev, [docType]: 0 }));

    try {
      const uploadedUrls = [];
      const folder = `sellers/${propertyId}/documents`;

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const result = await uploadToCloud(
          file,
          folder,
          docType,
          (progress) => {
            const totalProgress = Math.round(((i + progress / 100) / fileArray.length) * 100);
            setUploadProgress(prev => ({ ...prev, [docType]: totalProgress }));
          }
        );

        if (result.success) {
          uploadedUrls.push({
            url: result.url,
            public_id: result.public_id,
            filename: file.name,
            uploaded_at: new Date().toISOString()
          });
        } else {
          throw new Error(result.error);
        }
      }

      // Update documents state
      const newDocs = config.multiple ? uploadedUrls : uploadedUrls[0];
      setDocuments(prev => ({ ...prev, [docType]: newDocs }));

      // Save to backend
      await axios.post(`/seller/properties/${propertyId}/documents`, {
        [docType]: newDocs
      });

      toast.success('Documento caricato con successo');
    } catch (error) {
      console.error('Upload error:', error);
      setErrors(prev => ({ ...prev, [docType]: error.message || 'Errore nel caricamento' }));
      toast.error(error.message || 'Errore nel caricamento');
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
      setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
    }
  };

  const handleRemoveDocument = async (docType, index = null) => {
    const config = DOCUMENT_CONFIGS[docType];
    
    if (config.multiple && index !== null) {
      const newDocs = [...(documents[docType] || [])];
      newDocs.splice(index, 1);
      setDocuments(prev => ({ ...prev, [docType]: newDocs }));
      
      await axios.post(`/seller/properties/${propertyId}/documents`, {
        [docType]: newDocs
      });
    } else {
      setDocuments(prev => {
        const newDocs = { ...prev };
        delete newDocs[docType];
        return newDocs;
      });
      
      await axios.post(`/seller/properties/${propertyId}/documents`, {
        [docType]: null
      });
    }
    
    toast.success('Documento rimosso');
  };

  const handleSubmitForReview = async () => {
    try {
      await axios.put(`/seller/properties/${propertyId}/submit`);
      toast.success('Immobile inviato per revisione!');
      onComplete();
    } catch (error) {
      toast.error('Errore nell\'invio');
    }
  };

  return (
    <div className="space-y-6">
      {/* Photo Tutorial */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <Camera className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">Guida alle Foto</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Risoluzione minima: 1920px di larghezza</li>
              <li>• Risoluzione consigliata: 3000px o superiore</li>
              <li>• Formati accettati: JPG, PNG</li>
              <li>• <strong>Numero minimo foto: 15</strong> (almeno una per ambiente)</li>
              <li>• Dimensione massima: 20 MB per foto</li>
              <li>• Includi: esterni, interni di ogni stanza, bagni, cucina, balconi</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#1D1D1F]">Completezza Documentazione</h3>
          <span className="text-2xl font-bold text-[#0071e3]">{completion.percentage}%</span>
        </div>
        <Progress value={completion.percentage} className="h-3" />
        {completion.missing.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-[#86868B] mb-2">Documenti mancanti:</p>
            <div className="flex flex-wrap gap-2">
              {completion.missing.map((doc, i) => (
                <Badge key={i} variant="outline" className="text-orange-600 border-orange-300">
                  {doc}
                </Badge>
              ))}
            </div>
          </div>
        )}
        <p className="text-sm text-[#86868B] mt-4">
          Puoi inviare l'immobile anche con documentazione incompleta. Gli agenti ti aiuteranno a completarla.
        </p>
      </div>

      {/* Document Upload Cards */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h3 className="font-semibold text-[#1D1D1F] mb-6">Carica Documenti</h3>
        
        <div className="space-y-4">
          {documentTypes.map((doc) => {
            const isUploaded = doc.multiple 
              ? (documents[doc.key]?.length > 0) 
              : !!documents[doc.key];
            const isUploading = uploading[doc.key];
            const progress = uploadProgress[doc.key] || 0;
            const error = errors[doc.key];

            return (
              <div 
                key={doc.key} 
                className={`border rounded-lg p-4 transition-colors ${
                  error ? 'border-red-300 bg-red-50' : 
                  isUploaded ? 'border-green-300 bg-green-50' : 
                  'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {isUploaded ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    ) : (
                      <div className={`h-5 w-5 rounded-full border-2 mt-0.5 ${
                        error ? 'border-red-400' : 'border-gray-300'
                      }`} />
                    )}
                    <div>
                      <p className="font-medium text-[#1D1D1F]">
                        {doc.label}
                        {doc.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      <p className="text-sm text-[#86868B] mt-1">{doc.helpText}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Formati: {doc.acceptedFormats} | Max: {doc.maxSizeMB} MB
                      </p>
                      
                      {/* Error message */}
                      {error && (
                        <p className="text-sm text-red-600 mt-2 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {error}
                        </p>
                      )}

                      {/* Upload progress */}
                      {isUploading && (
                        <div className="mt-3">
                          <div className="flex items-center space-x-2">
                            <Loader2 className="h-4 w-4 animate-spin text-[#0071e3]" />
                            <span className="text-sm text-gray-600">Caricamento in corso...</span>
                          </div>
                          <Progress value={progress} className="h-2 mt-2" />
                        </div>
                      )}

                      {/* Uploaded files list */}
                      {isUploaded && !isUploading && (
                        <div className="mt-3 space-y-2">
                          {doc.multiple ? (
                            documents[doc.key].map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white rounded px-3 py-2 border">
                                <div className="flex items-center space-x-2">
                                  <File className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm text-gray-700">{file.filename || `File ${idx + 1}`}</span>
                                </div>
                                <button
                                  onClick={() => handleRemoveDocument(doc.key, idx)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center justify-between bg-white rounded px-3 py-2 border">
                              <div className="flex items-center space-x-2">
                                <File className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-700">
                                  {documents[doc.key]?.filename || 'Documento caricato'}
                                </span>
                              </div>
                              <button
                                onClick={() => handleRemoveDocument(doc.key)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Upload button */}
                  <div className="ml-4">
                    <input
                      type="file"
                      id={`upload-${doc.key}`}
                      accept={doc.acceptedFormats}
                      multiple={doc.multiple}
                      className="hidden"
                      onChange={(e) => handleFileSelect(doc.key, e.target.files)}
                      disabled={isUploading}
                    />
                    <label 
                      htmlFor={`upload-${doc.key}`}
                      className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 h-9 px-4 py-2 cursor-pointer ${
                        isUploading ? 'opacity-50 cursor-not-allowed' :
                        isUploaded 
                          ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground' 
                          : 'bg-[#0071e3] text-white hover:bg-[#0077ED]'
                      }`}
                    >
                      {isUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          {isUploaded ? 'Sostituisci' : 'Carica'}
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-between items-center pt-6 border-t">
        <div className="text-sm text-[#86868B]">
          Completezza: <span className="font-semibold">{completion.percentage}%</span>
        </div>
        <Button 
          onClick={handleSubmitForReview}
          className="bg-[#0071e3] hover:bg-[#005BB5] text-white"
          data-testid="submit-property-btn"
        >
          <Send className="h-4 w-4 mr-2" />
          Invia per Revisione
        </Button>
      </div>
    </div>
  );
};

// My Properties Page
const MyPropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/seller/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { label: 'Bozza', class: 'badge-pending' },
      'pending_review': { label: 'In Revisione WEQASA', class: 'badge-auction' },
      'approved': { label: 'Approvato - Seleziona Agenti', class: 'badge-active' },
      'proposed_to_agents': { label: 'Proposto - In attesa risposte', class: 'badge-auction' },
      'mandate_signed': { label: 'Mandato Firmato', class: 'badge-active' },
      'rejected': { label: 'Rifiutato', class: 'badge-closed' }
    };
    const s = statusMap[status] || { label: status, class: 'badge-pending' };
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[#1D1D1F] ">I Miei Immobili</h1>
        <Link to="/seller/carica">
          <Button className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Carica Immobile
          </Button>
        </Link>
      </div>

      {properties.length > 0 ? (
        <div className="space-y-4">
          {properties.map((prop) => (
            <div key={prop.property_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Building className="h-8 w-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[#1D1D1F]">{prop.address}</h3>
                    <p className="text-[#86868B]">{prop.city} ({prop.province}), {prop.postal_code}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                      {prop.sqm && <span>{prop.sqm} m²</span>}
                      {prop.rooms && <span>{prop.rooms} locali</span>}
                    </div>
                  </div>
                </div>
                {getStatusBadge(prop.status)}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-[#86868B]">Documenti:</span>
                    <Progress value={prop.document_completion_percentage || 0} className="w-32 h-2" />
                    <span className="text-sm font-medium">{prop.document_completion_percentage || 0}%</span>
                  </div>
                  <Link to={`/seller/immobili/${prop.property_id}`}>
                    <Button variant="outline" size="sm">
                      Dettagli <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessun immobile caricato</h3>
          <p className="text-[#86868B] mb-6">Inizia caricando il tuo primo immobile</p>
          <Link to="/seller/carica">
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Carica Immobile
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

// Proposals Page
const ProposalsPage = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [signingLoading, setSigningLoading] = useState(false);
  const [phoneLast4, setPhoneLast4] = useState('');

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await axios.get('/seller/proposals');
      setProposals(response.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const handleAccept = async (proposal) => {
    try {
      const response = await axios.post(`/seller/proposals/${proposal.proposal_id}/accept`);
      setPhoneLast4(response.data.phone);
      setSelectedProposal(proposal);
      setOtpDialogOpen(true);
      toast.success('Codice OTP inviato al tuo telefono');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'accettazione');
    }
  };

  const handleSignMandate = async () => {
    if (otp.length !== 6) {
      toast.error('Inserisci il codice completo');
      return;
    }

    setSigningLoading(true);
    try {
      await axios.post('/seller/mandates/sign', {
        proposal_id: selectedProposal.proposal_id,
        otp_code: otp
      });
      toast.success('Mandato firmato con successo!');
      setOtpDialogOpen(false);
      setOtp('');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice OTP non valido');
    } finally {
      setSigningLoading(false);
    }
  };

  const handleReject = async (proposalId) => {
    try {
      await axios.post(`/seller/proposals/${proposalId}/reject`);
      toast.success('Proposta rifiutata');
      fetchProposals();
    } catch (error) {
      toast.error('Errore nel rifiuto');
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Proposte degli Agenti</h1>

      {proposals.length > 0 ? (
        <div className="space-y-4">
          {proposals.map((proposal) => (
            <div key={proposal.proposal_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-[#1D1D1F]">
                    {proposal.agent?.company_name || 'Agenzia'}
                  </h3>
                  <p className="text-[#86868B]">
                    {proposal.agent?.first_name} {proposal.agent?.last_name}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Per: {proposal.property?.address}, {proposal.property?.city}
                  </p>
                </div>
                <Badge className={
                  proposal.status === 'pending' ? 'badge-pending' :
                  proposal.status === 'accepted' ? 'badge-active' :
                  proposal.status === 'pending_signature' ? 'badge-auction' : 'badge-closed'
                }>
                  {proposal.status === 'pending' ? 'In Attesa' :
                   proposal.status === 'accepted' ? 'Accettata' :
                   proposal.status === 'pending_signature' ? 'In Firma' : 'Rifiutata'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-4">
                <div>
                  <p className="text-sm text-[#86868B]">Valore Stimato</p>
                  <p className="font-semibold text-[#1D1D1F]">{formatPrice(proposal.estimated_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Prezzo Base Gara</p>
                  <p className="font-semibold text-[#0071e3]">{formatPrice(proposal.auction_start_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Prezzo Minimo</p>
                  <p className="font-semibold text-[#1D1D1F]">{formatPrice(proposal.reserve_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Commissione</p>
                  <p className="font-semibold text-[#1D1D1F]">{proposal.commission_percentage}%</p>
                </div>
              </div>

              {proposal.message && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">{proposal.message}</p>
                </div>
              )}

              {proposal.status === 'pending' && (
                <div className="flex space-x-3">
                  <Button onClick={() => handleAccept(proposal)} className="btn-primary">
                    <Check className="h-4 w-4 mr-2" />
                    Accetta e Firma Mandato
                  </Button>
                  <Button variant="outline" onClick={() => handleReject(proposal.proposal_id)} className="text-red-600 border-red-200">
                    <X className="h-4 w-4 mr-2" />
                    Rifiuta
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna proposta ricevuta</h3>
          <p className="text-[#86868B]">
            Quando il tuo immobile sarà approvato, gli agenti potranno inviarti proposte
          </p>
        </div>
      )}

      {/* OTP Signature Dialog */}
      <Dialog open={otpDialogOpen} onOpenChange={setOtpDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <PenTool className="h-5 w-5 text-[#0071e3]" />
              <span>Firma Digitale Mandato</span>
            </DialogTitle>
            <DialogDescription>
              Verifica la tua identità inserendo il codice OTP per firmare il mandato di vendita.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-gray-600">
              Inserisci il codice OTP a 6 cifre inviato al numero che termina con ****{phoneLast4}
            </p>
            
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-[#1D1D1F]">
                Confermando, firmerai digitalmente il mandato di vendita e l'agente selezionato potrà gestire la gara di vendita del tuo immobile.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOtpDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSignMandate} 
              disabled={signingLoading || otp.length !== 6}
              className="btn-primary"
            >
              {signingLoading ? 'Firma in corso...' : 'Conferma e Firma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Seller Gare Page - Gare di Vendita sui propri immobili
const SellerGarePage = () => {
  const navigate = useNavigate();
  const [gare, setGare] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGare();
  }, []);

  const fetchGare = async () => {
    try {
      const response = await axios.get('/seller/gare');
      setGare(response.data || []);
    } catch (error) {
      console.error('Error fetching gare:', error);
      toast.error('Errore nel caricamento delle gare');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { class: 'bg-blue-100 text-blue-800', label: 'Programmata' },
      active: { class: 'bg-green-100 text-green-800', label: 'In Corso' },
      closing: { class: 'bg-yellow-100 text-[#1D1D1F]', label: 'In Chiusura' },
      suspended: { class: 'bg-red-100 text-red-800', label: 'Sospesa' },
      awarded: { class: 'bg-purple-100 text-purple-800', label: 'Aggiudicata' },
      not_awarded: { class: 'bg-gray-100 text-gray-800', label: 'Non Aggiudicata' },
      under_review: { class: 'bg-orange-100 text-orange-800', label: 'In Revisione' },
      cancelled: { class: 'bg-red-100 text-red-800', label: 'Annullata' }
    };
    const config = statusConfig[status] || { class: 'bg-gray-100', label: status };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seller-gare-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Gare di Vendita</h1>
          <p className="text-[#86868B] mt-1">Monitora le gare di vendita sui tuoi immobili</p>
        </div>
        <Link to="/gare">
          <Button variant="outline" className="border-[#0071e3] text-[#0071e3]">
            <Eye className="h-4 w-4 mr-2" />
            Tutte le Gare
          </Button>
        </Link>
      </div>

      {gare.length > 0 ? (
        <div className="space-y-4">
          {gare.map((gara) => (
            <div 
              key={gara.competition_id} 
              className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`seller-gara-${gara.competition_id}`}
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex items-start space-x-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {gara.property?.photos?.[0] ? (
                      <img src={gara.property.photos[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[#1D1D1F]">
                      {gara.property?.address || 'Immobile'}
                    </h3>
                    <p className="text-[#86868B] flex items-center text-sm mt-1">
                      <MapPin className="h-4 w-4 mr-1" />
                      {gara.property?.city}
                    </p>
                    {gara.agent && (
                      <p className="text-sm text-gray-400 mt-1">
                        Agente: {gara.agent.first_name} {gara.agent.last_name} ({gara.agent.company_name})
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-start md:items-end gap-2">
                  {getStatusBadge(gara.status)}
                  {gara.status === 'awarded' && gara.final_price && (
                    <div className="text-right">
                      <span className="text-sm text-[#86868B]">Venduto a</span>
                      <p className="font-bold text-green-600">{formatPrice(gara.final_price)}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                <div>
                  <span className="text-xs text-[#86868B] block">Valore Immobile</span>
                  <p className="font-semibold text-[#1D1D1F]">{formatPrice(gara.valore_immobile)}</p>
                </div>
                <div>
                  <span className="text-xs text-[#86868B] block">Base di Gara</span>
                  <p className="font-semibold text-[#0071e3]">{formatPrice(gara.base_di_gara)}</p>
                </div>
                <div>
                  <span className="text-xs text-[#86868B] block">Offerta Attuale</span>
                  <p className="font-semibold text-green-600">
                    {gara.current_highest_bid ? formatPrice(gara.current_highest_bid) : '-'}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-[#86868B] block">Offerte</span>
                  <p className="font-semibold text-[#1D1D1F] flex items-center">
                    <TrendingUp className="h-4 w-4 mr-1 text-gray-400" />
                    {gara.total_bids || 0}
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/gara/${gara.competition_id}`)}
                  className="border-[#0071e3] text-[#0071e3]"
                  data-testid={`view-gara-${gara.competition_id}`}
                >
                  Visualizza Gara
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Gavel className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna gara attiva</h3>
          <p className="text-[#86868B] mb-2">
            Non ci sono ancora gare di vendita attive sui tuoi immobili.
          </p>
          <p className="text-sm text-gray-400">
            Le gare vengono create dagli agenti dopo la firma del mandato.
          </p>
        </div>
      )}
    </div>
  );
};

// Mandates Page
const MandatesPage = () => {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMandates();
  }, []);

  const fetchMandates = async () => {
    try {
      const response = await axios.get('/seller/mandates');
      setMandates(response.data);
    } catch (error) {
      console.error('Error fetching mandates:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Mandati Firmati</h1>

      {mandates.length > 0 ? (
        <div className="space-y-4">
          {mandates.map((mandate) => (
            <div key={mandate.mandate_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
              <div className="flex items-center justify-between mb-4">
                <Badge className="badge-active">Mandato Attivo</Badge>
                <span className="text-sm text-[#86868B]">
                  Firmato il {mandate.signed_at ? format(new Date(mandate.signed_at), 'dd/MM/yyyy', { locale: it }) : '-'}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-[#1D1D1F] mb-2">Immobile</h4>
                  <p className="text-gray-600">
                    {mandate.property?.address}, {mandate.property?.city}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-[#1D1D1F] mb-2">Agente Incaricato</h4>
                  <p className="text-gray-600">
                    {mandate.agent?.company_name}<br />
                    {mandate.agent?.first_name} {mandate.agent?.last_name}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-[#86868B]">Valore Stimato</p>
                  <p className="font-semibold">{formatPrice(mandate.terms?.estimated_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Prezzo Base Gara</p>
                  <p className="font-semibold text-[#0071e3]">{formatPrice(mandate.terms?.auction_start_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Commissione</p>
                  <p className="font-semibold">{mandate.terms?.commission_percentage}%</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessun mandato firmato</h3>
          <p className="text-[#86868B]">
            Quando accetterai una proposta e firmerai il mandato, apparirà qui
          </p>
        </div>
      )}
    </div>
  );
};

// Notifications Page
const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) {
      markAsRead(notif.notification_id);
    }
    // Navigate to property detail if action_url exists
    if (notif.data?.action_url) {
      navigate(notif.data.action_url);
    } else if (notif.data?.property_id) {
      navigate(`/seller/immobili/${notif.data.property_id}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'property_approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'proposal_received':
        return <Send className="h-5 w-5 text-blue-500" />;
      case 'mandate_signed':
        return <PenTool className="h-5 w-5 text-indigo-500" />;
      default:
        return <Bell className="h-5 w-5 text-[#86868B]" />;
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Notifiche</h1>

      {notifications.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
          <div className="divide-y divide-gray-100">
            {notifications.map((notif) => (
              <div 
                key={notif.notification_id} 
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${!notif.read ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : ''}`}
                onClick={() => handleNotificationClick(notif)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    !notif.read ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    {getNotificationIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-medium ${!notif.read ? 'text-[#1D1D1F]' : 'text-gray-700'}`}>
                          {notif.title}
                        </p>
                        <p className="text-gray-600 text-sm mt-1">{notif.message}</p>
                      </div>
                      {!notif.read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-gray-400">
                        {notif.created_at ? format(new Date(notif.created_at), 'dd/MM/yyyy HH:mm', { locale: it }) : ''}
                      </p>
                      {(notif.data?.action_url || notif.data?.property_id) && (
                        <Button size="sm" variant="outline" className="text-xs">
                          <ChevronRight className="h-3 w-3 mr-1" />
                          Vai al dettaglio
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna notifica</h3>
          <p className="text-[#86868B]">Le tue notifiche appariranno qui</p>
        </div>
      )}
    </div>
  );
};

// Profile Page
const SellerProfilePage = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Il Mio Profilo</h1>
      
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-[#86868B]">Nome</label>
            <p className="font-medium text-[#1D1D1F]">{user?.first_name}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Cognome</label>
            <p className="font-medium text-[#1D1D1F]">{user?.last_name}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Email</label>
            <p className="font-medium text-[#1D1D1F]">{user?.email}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Telefono</label>
            <p className="font-medium text-[#1D1D1F]">{user?.phone || 'Non inserito'}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Codice Fiscale</label>
            <p className="font-medium text-[#1D1D1F]">{user?.tax_code || 'Non inserito'}</p>
          </div>
          <div>
            <label className="text-sm text-[#86868B]">Stato Account</label>
            <Badge className={user?.verification_status === 'verified' ? 'badge-active' : 'badge-pending'}>
              {user?.verification_status || 'Registrato'}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

// Photo Thumbnail Component with error handling
const PhotoThumbnail = ({ photo, index, onRemove }) => {
  const [imageError, setImageError] = useState(false);
  
  // Check if URL is a data URL (base64) or a valid URL
  const isDataUrl = photo.url?.startsWith('data:');
  const isLocalUrl = photo.url?.startsWith('local://');
  const isSimulatedUrl = photo.url?.includes('storage.weqasa.it');
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  const handleView = () => {
    if (isDataUrl) {
      // Open data URL in new tab
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`<img src="${photo.url}" style="max-width:100%;height:auto;" />`);
      }
    } else if (!isSimulatedUrl && !isLocalUrl && !imageError) {
      window.open(photo.url, '_blank');
    }
  };
  
  const showPlaceholder = imageError || isLocalUrl || (isSimulatedUrl && !isDataUrl);
  
  return (
    <div className="relative group aspect-video rounded-lg overflow-hidden border bg-gray-100">
      {showPlaceholder ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
          <Camera className="h-8 w-8 text-gray-400 mb-2" />
          <span className="text-xs text-[#86868B] text-center px-2">{photo.filename || `Foto ${index + 1}`}</span>
        </div>
      ) : (
        <img 
          src={photo.url}
          alt={`Foto ${index + 1}`}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      )}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
        {!showPlaceholder && (
          <Button
            size="sm"
            variant="secondary"
            onClick={handleView}
          >
            <Eye className="h-4 w-4" />
          </Button>
        )}
        <Button
          size="sm"
          variant="destructive"
          onClick={() => onRemove(index)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
        {index + 1}
      </div>
    </div>
  );
};

// Agent Selection Component for Approved Property Status
const AgentSelectionForApprovedProperty = ({ propertyId, onProposalsSent }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAgentProfile, setSelectedAgentProfile] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/seller/available-agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Errore nel caricamento degli agenti');
    } finally {
      setLoading(false);
    }
  };

  const toggleAgent = (agentId) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const openAgentProfile = (agent, e) => {
    e.stopPropagation();
    setSelectedAgentProfile(agent);
    setShowAgentModal(true);
  };

  const handleSubmitProposals = async () => {
    if (selectedAgents.length === 0) {
      toast.error('Seleziona almeno un\'agenzia');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/seller/properties/${propertyId}/propose-to-agents`, {
        agent_ids: selectedAgents
      });
      onProposalsSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio delle proposte');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Immobile Approvato!</p>
            <p className="text-sm text-green-700">
              Seleziona le agenzie immobiliari a cui proporre il tuo immobile. 
              Riceveranno la scheda completa e potranno inviarti la loro valutazione.
            </p>
          </div>
        </div>
      </div>

      {/* Agent List */}
      {agents.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-medium text-[#1D1D1F]">
            Seleziona una o più agenzie ({selectedAgents.length} selezionate su {agents.length} disponibili)
          </p>
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
            {agents.map((agent) => (
              <div
                key={agent.user_id}
                className={`border rounded-xl overflow-hidden transition-all ${
                  selectedAgents.includes(agent.user_id)
                    ? 'border-[#0071e3] ring-2 ring-[#0071e3]/20'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Agent Cover Image */}
                <div className="relative h-24 bg-gradient-to-r from-[#0071e3] to-[#00b4d8]">
                  {agent.cover_image && (
                    <img 
                      src={agent.cover_image} 
                      alt="Cover" 
                      className="w-full h-full object-cover"
                    />
                  )}
                  {/* Selection Checkbox Overlay */}
                  <div 
                    className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                      selectedAgents.includes(agent.user_id)
                        ? 'bg-[#0071e3] border-[#0071e3]'
                        : 'bg-white/90 border-gray-400 hover:border-[#0071e3]'
                    }`}
                    onClick={() => toggleAgent(agent.user_id)}
                  >
                    {selectedAgents.includes(agent.user_id) && (
                      <Check className="h-4 w-4 text-white" />
                    )}
                  </div>
                </div>
                
                {/* Agent Info */}
                <div className="p-4 relative">
                  {/* Logo positioned over the cover */}
                  <div className="absolute -top-10 left-4">
                    <div className="w-16 h-16 rounded-xl border-4 border-white bg-white shadow-lg overflow-hidden">
                      {agent.logo || agent.profile_image ? (
                        <img 
                          src={agent.logo || agent.profile_image} 
                          alt={agent.company_name || agent.first_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#0071e3] to-[#00b4d8] flex items-center justify-center">
                          <Building className="h-8 w-8 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Content with padding for logo */}
                  <div className="ml-20 pt-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-[#1D1D1F] text-lg">
                            {agent.company_name || `${agent.first_name} ${agent.last_name}`}
                          </h4>
                          {agent.verification_status === 'approved' && (
                            <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              <Award className="h-3 w-3" />
                              Verificato
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-[#86868B] mt-0.5">
                          {agent.city || 'Italia'} {agent.province && `(${agent.province})`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {agent.rating && (
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          <span className="font-medium">{agent.rating.toFixed(1)}</span>
                          <span className="text-[#86868B]">({agent.review_count || 0} recensioni)</span>
                        </div>
                      )}
                      {agent.completed_sales > 0 && (
                        <div className="flex items-center gap-1 text-sm text-[#86868B]">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {agent.completed_sales} vendite
                        </div>
                      )}
                      {agent.experience_years && (
                        <div className="text-sm text-[#86868B]">
                          {agent.experience_years} anni esperienza
                        </div>
                      )}
                    </div>
                    
                    {/* Specializations */}
                    {agent.specializations && agent.specializations.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {agent.specializations.slice(0, 3).map((spec, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[#0071e3] border-[#0071e3]"
                      onClick={(e) => openAgentProfile(agent, e)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Vedi Profilo Completo
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedAgents.includes(agent.user_id) ? "default" : "outline"}
                      className={selectedAgents.includes(agent.user_id) 
                        ? "bg-[#0071e3] hover:bg-[#005BB5]" 
                        : "border-gray-300"
                      }
                      onClick={() => toggleAgent(agent.user_id)}
                    >
                      {selectedAgents.includes(agent.user_id) ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Selezionata
                        </>
                      ) : (
                        'Seleziona'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-6">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-[#86868B]">Nessuna agenzia disponibile al momento</p>
        </div>
      )}

      {/* Submit Button */}
      {agents.length > 0 && (
        <Button
          onClick={handleSubmitProposals}
          disabled={selectedAgents.length === 0 || submitting}
          className="w-full bg-[#0071e3] hover:bg-[#005BB5] h-12 text-base"
        >
          {submitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Invio in corso...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Invia Proposta a {selectedAgents.length} Agenzia/e
            </>
          )}
        </Button>
      )}

      {/* Agent Profile Modal */}
      <Dialog open={showAgentModal} onOpenChange={setShowAgentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {selectedAgentProfile && (
            <>
              {/* Cover Image */}
              <div className="relative h-48 bg-gradient-to-r from-[#0071e3] to-[#00b4d8]">
                {selectedAgentProfile.cover_image && (
                  <img 
                    src={selectedAgentProfile.cover_image} 
                    alt="Cover" 
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                
                {/* Close button */}
                <button 
                  onClick={() => setShowAgentModal(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Logo and Basic Info */}
              <div className="relative px-6 pb-6">
                <div className="absolute -top-16 left-6">
                  <div className="w-28 h-28 rounded-2xl border-4 border-white bg-white shadow-xl overflow-hidden">
                    {selectedAgentProfile.logo || selectedAgentProfile.profile_image ? (
                      <img 
                        src={selectedAgentProfile.logo || selectedAgentProfile.profile_image} 
                        alt={selectedAgentProfile.company_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#0071e3] to-[#00b4d8] flex items-center justify-center">
                        <Building className="h-12 w-12 text-white" />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="pt-16">
                  {/* Name and Verification */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-[#1D1D1F]">
                        {selectedAgentProfile.company_name || `${selectedAgentProfile.first_name} ${selectedAgentProfile.last_name}`}
                      </h2>
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin className="h-4 w-4 text-[#86868B]" />
                        <span className="text-[#86868B]">
                          {selectedAgentProfile.registered_address || selectedAgentProfile.city || 'Italia'}
                          {selectedAgentProfile.province && ` (${selectedAgentProfile.province})`}
                        </span>
                      </div>
                    </div>
                    {selectedAgentProfile.verification_status === 'approved' && (
                      <div className="flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-sm font-medium">
                        <Award className="h-4 w-4" />
                        Verificato WEQASA
                      </div>
                    )}
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 text-2xl font-bold text-[#1D1D1F]">
                        <Star className="h-6 w-6 text-yellow-500 fill-yellow-500" />
                        {selectedAgentProfile.rating?.toFixed(1) || '4.5'}
                      </div>
                      <p className="text-sm text-[#86868B] mt-1">
                        {selectedAgentProfile.review_count || 0} recensioni
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-[#1D1D1F]">
                        {selectedAgentProfile.completed_sales || 0}
                      </div>
                      <p className="text-sm text-[#86868B] mt-1">Vendite completate</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-[#1D1D1F]">
                        {selectedAgentProfile.experience_years || '-'}
                      </div>
                      <p className="text-sm text-[#86868B] mt-1">Anni di esperienza</p>
                    </div>
                  </div>
                  
                  {/* Description */}
                  {selectedAgentProfile.bio && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#1D1D1F] mb-2">Chi Siamo</h3>
                      <p className="text-[#86868B] leading-relaxed">
                        {selectedAgentProfile.bio}
                      </p>
                    </div>
                  )}
                  
                  {/* Specializations */}
                  {selectedAgentProfile.specializations && selectedAgentProfile.specializations.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#1D1D1F] mb-2">Specializzazioni</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedAgentProfile.specializations.map((spec, idx) => (
                          <span key={idx} className="bg-[#0071e3]/10 text-[#0071e3] px-3 py-1.5 rounded-full text-sm font-medium">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Contact Info */}
                  <div className="mt-6">
                    <h3 className="font-semibold text-[#1D1D1F] mb-3">Informazioni di Contatto</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedAgentProfile.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Mail className="h-4 w-4 text-[#86868B]" />
                          </div>
                          <span className="text-[#86868B]">{selectedAgentProfile.email}</span>
                        </div>
                      )}
                      {selectedAgentProfile.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Phone className="h-4 w-4 text-[#86868B]" />
                          </div>
                          <span className="text-[#86868B]">{selectedAgentProfile.phone}</span>
                        </div>
                      )}
                      {selectedAgentProfile.rea_number && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <FileText className="h-4 w-4 text-[#86868B]" />
                          </div>
                          <span className="text-[#86868B]">REA: {selectedAgentProfile.rea_number}</span>
                        </div>
                      )}
                      {selectedAgentProfile.vat_number && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Building className="h-4 w-4 text-[#86868B]" />
                          </div>
                          <span className="text-[#86868B]">P.IVA: {selectedAgentProfile.vat_number}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Certificates/Achievements */}
                  {selectedAgentProfile.weqasa_certified && (
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                          <Award className="h-6 w-6 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-amber-800">Agente Certificato WEQASA</h4>
                          <p className="text-sm text-amber-700">
                            Questa agenzia ha superato il processo di verifica WEQASA ed è certificata per operare sulla piattaforma.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Button */}
                  <div className="mt-6 flex gap-3">
                    <Button 
                      className="flex-1 bg-[#0071e3] hover:bg-[#005BB5] h-12"
                      onClick={() => {
                        toggleAgent(selectedAgentProfile.user_id);
                        setShowAgentModal(false);
                      }}
                    >
                      {selectedAgents.includes(selectedAgentProfile.user_id) ? (
                        <>
                          <X className="h-5 w-5 mr-2" />
                          Rimuovi dalla Selezione
                        </>
                      ) : (
                        <>
                          <Check className="h-5 w-5 mr-2" />
                          Seleziona questa Agenzia
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      className="h-12"
                      onClick={() => setShowAgentModal(false)}
                    >
                      Chiudi
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Proposed Agents Section - Shows contacted agents + allows adding more
const ProposedAgentsSection = ({ property, proposals, onRefresh, onViewProposals }) => {
  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMore, setShowAddMore] = useState(false);
  const [selectedNewAgents, setSelectedNewAgents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAgentProfile, setSelectedAgentProfile] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);

  const proposedAgentIds = property.proposed_agent_ids || [];

  useEffect(() => {
    fetchAllAgents();
  }, []);

  const fetchAllAgents = async () => {
    try {
      const response = await axios.get('/seller/available-agents');
      setAllAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Separate agents into contacted and not contacted
  const contactedAgents = allAgents.filter(a => proposedAgentIds.includes(a.user_id));
  const availableAgents = allAgents.filter(a => !proposedAgentIds.includes(a.user_id));

  const toggleNewAgent = (agentId) => {
    setSelectedNewAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const handleSendToMoreAgents = async () => {
    if (selectedNewAgents.length === 0) {
      toast.error('Seleziona almeno un\'agenzia');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/seller/properties/${property.property_id}/propose-to-agents`, {
        agent_ids: selectedNewAgents
      });
      toast.success(`Proposta inviata a ${selectedNewAgents.length} nuova/e agenzia/e!`);
      setSelectedNewAgents([]);
      setShowAddMore(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio delle proposte');
    } finally {
      setSubmitting(false);
    }
  };

  const getProposalStatus = (agentId) => {
    const proposal = proposals.find(p => p.agent_id === agentId);
    if (!proposal) return { status: 'pending', label: 'In attesa', color: 'bg-yellow-100 text-yellow-800' };
    if (proposal.status === 'accepted') return { status: 'accepted', label: 'Accettata', color: 'bg-green-100 text-green-800' };
    if (proposal.status === 'rejected') return { status: 'rejected', label: 'Rifiutata', color: 'bg-red-100 text-red-800' };
    return { status: 'pending', label: 'In attesa', color: 'bg-yellow-100 text-yellow-800' };
  };

  const openAgentProfile = (agent, e) => {
    if (e) e.stopPropagation();
    setSelectedAgentProfile(agent);
    setShowAgentModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Send className="h-5 w-5 text-[#0071e3] mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Proposte Inviate</p>
            <p className="text-sm text-blue-700">
              Hai proposto il tuo immobile a {contactedAgents.length} agenzia/e. 
              Puoi ancora contattare altre {availableAgents.length} agenzie disponibili.
            </p>
          </div>
        </div>
      </div>

      {/* Contacted Agents Section */}
      <div>
        <h4 className="font-semibold text-[#1D1D1F] mb-3 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          Agenzie Contattate ({contactedAgents.length})
        </h4>
        <div className="space-y-2">
          {contactedAgents.map((agent) => {
            const proposalStatus = getProposalStatus(agent.user_id);
            return (
              <div 
                key={agent.user_id}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-gradient-to-br from-[#0071e3] to-[#00b4d8] flex items-center justify-center">
                    {agent.logo || agent.profile_image ? (
                      <img src={agent.logo || agent.profile_image} alt={agent.company_name} className="w-full h-full object-cover" />
                    ) : (
                      <Building className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#1D1D1F]">
                        {agent.company_name || `${agent.first_name} ${agent.last_name}`}
                      </p>
                      {agent.verification_status === 'approved' && (
                        <Award className="h-4 w-4 text-amber-500" />
                      )}
                    </div>
                    <p className="text-sm text-[#86868B]">{agent.city || 'Italia'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${proposalStatus.color}`}>
                    {proposalStatus.label}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[#0071e3]"
                    onClick={(e) => openAgentProfile(agent, e)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* View Proposals Button */}
      {proposals.length > 0 && (
        <Button 
          onClick={onViewProposals}
          className="w-full bg-[#0071e3] hover:bg-[#005BB5]"
        >
          <Users className="h-4 w-4 mr-2" />
          Visualizza proposte ricevute ({proposals.length})
        </Button>
      )}

      {/* Add More Agents Section */}
      {availableAgents.length > 0 && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-[#1D1D1F] flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#0071e3]" />
              Altre Agenzie Disponibili ({availableAgents.length})
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddMore(!showAddMore)}
              className="text-[#0071e3] border-[#0071e3]"
            >
              {showAddMore ? 'Nascondi' : 'Mostra'}
            </Button>
          </div>

          {showAddMore && (
            <div className="space-y-3">
              <p className="text-sm text-[#86868B]">
                Seleziona altre agenzie a cui proporre il tuo immobile:
              </p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {availableAgents.map((agent) => (
                  <div
                    key={agent.user_id}
                    className={`border rounded-xl overflow-hidden transition-all ${
                      selectedNewAgents.includes(agent.user_id)
                        ? 'border-[#0071e3] ring-2 ring-[#0071e3]/20'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Mini Cover */}
                    <div className="relative h-16 bg-gradient-to-r from-[#0071e3] to-[#00b4d8]">
                      {agent.cover_image && (
                        <img src={agent.cover_image} alt="Cover" className="w-full h-full object-cover" />
                      )}
                      <div 
                        className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                          selectedNewAgents.includes(agent.user_id)
                            ? 'bg-[#0071e3] border-[#0071e3]'
                            : 'bg-white/90 border-gray-400 hover:border-[#0071e3]'
                        }`}
                        onClick={() => toggleNewAgent(agent.user_id)}
                      >
                        {selectedNewAgents.includes(agent.user_id) && (
                          <Check className="h-4 w-4 text-white" />
                        )}
                      </div>
                    </div>
                    
                    <div className="p-3 relative">
                      {/* Logo */}
                      <div className="absolute -top-8 left-3">
                        <div className="w-12 h-12 rounded-xl border-2 border-white bg-white shadow-md overflow-hidden">
                          {agent.logo || agent.profile_image ? (
                            <img src={agent.logo || agent.profile_image} alt={agent.company_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#0071e3] to-[#00b4d8] flex items-center justify-center">
                              <Building className="h-5 w-5 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-14 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-[#1D1D1F]">
                              {agent.company_name || `${agent.first_name} ${agent.last_name}`}
                            </p>
                            {agent.verification_status === 'approved' && (
                              <span className="flex items-center gap-0.5 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs">
                                <Award className="h-3 w-3" />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#86868B]">
                            <span>{agent.city || 'Italia'}</span>
                            {agent.rating && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-0.5">
                                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                                  {agent.rating.toFixed(1)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#0071e3] text-xs h-8"
                            onClick={(e) => openAgentProfile(agent, e)}
                          >
                            Profilo
                          </Button>
                          <Button
                            size="sm"
                            variant={selectedNewAgents.includes(agent.user_id) ? "default" : "outline"}
                            className={`h-8 text-xs ${selectedNewAgents.includes(agent.user_id) 
                              ? "bg-[#0071e3] hover:bg-[#005BB5]" 
                              : "border-gray-300"
                            }`}
                            onClick={() => toggleNewAgent(agent.user_id)}
                          >
                            {selectedNewAgents.includes(agent.user_id) ? (
                              <><Check className="h-3 w-3 mr-1" /> Selezionata</>
                            ) : (
                              'Seleziona'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Submit Button for new agents */}
              {selectedNewAgents.length > 0 && (
                <Button
                  onClick={handleSendToMoreAgents}
                  disabled={submitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white mt-3"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Invia Proposta a {selectedNewAgents.length} Nuova/e Agenzia/e
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Agent Profile Modal (same as in AgentSelectionForApprovedProperty) */}
      <Dialog open={showAgentModal} onOpenChange={setShowAgentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {selectedAgentProfile && (
            <>
              <div className="relative h-48 bg-gradient-to-r from-[#0071e3] to-[#00b4d8]">
                {selectedAgentProfile.cover_image && (
                  <img src={selectedAgentProfile.cover_image} alt="Cover" className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <button 
                  onClick={() => setShowAgentModal(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="relative px-6 pb-6">
                <div className="absolute -top-16 left-6">
                  <div className="w-28 h-28 rounded-2xl border-4 border-white bg-white shadow-xl overflow-hidden">
                    {selectedAgentProfile.logo || selectedAgentProfile.profile_image ? (
                      <img src={selectedAgentProfile.logo || selectedAgentProfile.profile_image} alt={selectedAgentProfile.company_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#0071e3] to-[#00b4d8] flex items-center justify-center">
                        <Building className="h-12 w-12 text-white" />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="pt-16">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-[#1D1D1F]">
                        {selectedAgentProfile.company_name || `${selectedAgentProfile.first_name} ${selectedAgentProfile.last_name}`}
                      </h2>
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin className="h-4 w-4 text-[#86868B]" />
                        <span className="text-[#86868B]">
                          {selectedAgentProfile.registered_address || selectedAgentProfile.city || 'Italia'}
                        </span>
                      </div>
                    </div>
                    {selectedAgentProfile.verification_status === 'approved' && (
                      <div className="flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-sm font-medium">
                        <Award className="h-4 w-4" />
                        Verificato WEQASA
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="flex items-center justify-center gap-1 text-2xl font-bold text-[#1D1D1F]">
                        <Star className="h-6 w-6 text-yellow-500 fill-yellow-500" />
                        {selectedAgentProfile.rating?.toFixed(1) || '4.5'}
                      </div>
                      <p className="text-sm text-[#86868B] mt-1">{selectedAgentProfile.review_count || 0} recensioni</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-[#1D1D1F]">{selectedAgentProfile.completed_sales || 0}</div>
                      <p className="text-sm text-[#86868B] mt-1">Vendite</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-[#1D1D1F]">{selectedAgentProfile.experience_years || '-'}</div>
                      <p className="text-sm text-[#86868B] mt-1">Anni exp.</p>
                    </div>
                  </div>
                  
                  {selectedAgentProfile.bio && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#1D1D1F] mb-2">Chi Siamo</h3>
                      <p className="text-[#86868B] leading-relaxed">{selectedAgentProfile.bio}</p>
                    </div>
                  )}
                  
                  {selectedAgentProfile.specializations && selectedAgentProfile.specializations.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-[#1D1D1F] mb-2">Specializzazioni</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedAgentProfile.specializations.map((spec, idx) => (
                          <span key={idx} className="bg-[#0071e3]/10 text-[#0071e3] px-3 py-1.5 rounded-full text-sm font-medium">{spec}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-6">
                    <h3 className="font-semibold text-[#1D1D1F] mb-3">Contatti</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedAgentProfile.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Mail className="h-4 w-4 text-[#86868B]" />
                          </div>
                          <span className="text-[#86868B]">{selectedAgentProfile.email}</span>
                        </div>
                      )}
                      {selectedAgentProfile.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Phone className="h-4 w-4 text-[#86868B]" />
                          </div>
                          <span className="text-[#86868B]">{selectedAgentProfile.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-6 flex gap-3">
                    <Button variant="outline" className="flex-1 h-12" onClick={() => setShowAgentModal(false)}>
                      Chiudi
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Property Detail Page - Full Implementation
const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('summary');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [proposalDetailOpen, setProposalDetailOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [signing, setSigning] = useState(false);
  
  // Comparison view state
  const [comparisonView, setComparisonView] = useState(false);
  const [confirmAgentDialogOpen, setConfirmAgentDialogOpen] = useState(false);
  
  // Agency Profile Dialog state
  const [agencyProfileOpen, setAgencyProfileOpen] = useState(false);
  const [agencyProfileData, setAgencyProfileData] = useState(null);
  const [loadingAgencyProfile, setLoadingAgencyProfile] = useState(false);
  
  // Photo management state
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  
  // Document management state
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [docUploadProgress, setDocUploadProgress] = useState(0);

  // Edit form state
  const [editForm, setEditForm] = useState({});

  // Helper function to open document preview
  const openDocumentPreview = (url, filename) => {
    if (!url) {
      toast.error('URL del documento non disponibile');
      return;
    }
    
    // Check if it's a data URL (base64)
    const isDataUrl = url.startsWith('data:');
    // Check if it's a simulated local URL
    const isLocalUrl = url.startsWith('local://');
    
    if (isDataUrl) {
      // For data URLs (base64 encoded files), open in a new window with proper rendering
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        // Check if it's a PDF data URL
        if (url.includes('application/pdf') || (filename && filename.toLowerCase().endsWith('.pdf'))) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${filename || 'Documento PDF'}</title>
                <style>
                  body { margin: 0; padding: 0; height: 100vh; }
                  iframe { width: 100%; height: 100%; border: none; }
                </style>
              </head>
              <body>
                <iframe src="${url}"></iframe>
              </body>
            </html>
          `);
        } else {
          // For images
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${filename || 'Documento'}</title>
                <style>
                  body { margin: 0; padding: 20px; background: #f5f5f5; display: flex; justify-content: center; }
                  img { max-width: 100%; height: auto; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
                </style>
              </head>
              <body>
                <img src="${url}" alt="${filename || 'Documento'}" />
              </body>
            </html>
          `);
        }
        newWindow.document.close();
      } else {
        toast.error('Impossibile aprire la finestra. Controlla le impostazioni del browser per i popup.');
      }
    } else if (isLocalUrl) {
      // For simulated local URLs, show a message
      toast.info('Anteprima non disponibile per file locali. Il documento sarà disponibile dopo il caricamento su cloud.');
    } else {
      // For real URLs (Cloudinary etc.), open directly
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    fetchPropertyDetails();
  }, [propertyId]);

  const fetchPropertyDetails = async () => {
    try {
      setLoading(true);
      const [propRes, proposalsRes, mandatesRes] = await Promise.all([
        axios.get('/seller/properties'),
        axios.get('/seller/proposals'),
        axios.get('/seller/mandates')
      ]);
      
      const prop = propRes.data.find(p => p.property_id === propertyId);
      if (prop) {
        setProperty(prop);
        setEditForm({
          address: prop.address,
          city: prop.city,
          province: prop.province,
          postal_code: prop.postal_code,
          property_type: prop.property_type,
          sqm: prop.sqm,
          rooms: prop.rooms,
          floor: prop.floor,
          condition: prop.condition || 'buono',
          energy_class: prop.energy_class || 'G',
          description: prop.description
        });
      }
      
      const propProposals = proposalsRes.data.filter(p => p.property_id === propertyId);
      setProposals(propProposals);
      
      const propMandates = mandatesRes.data.filter(m => m.property_id === propertyId);
      setMandates(propMandates);
    } catch (error) {
      console.error('Error fetching property details:', error);
      toast.error('Errore nel caricamento dei dettagli');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return format(new Date(dateStr), 'dd MMM yyyy', { locale: it });
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'draft': { label: 'Bozza', color: 'bg-gray-100 text-gray-800', icon: FileText },
      'pending_review': { label: 'In Revisione WEQASA', color: 'bg-yellow-100 text-[#1D1D1F]', icon: Clock },
      'approved': { label: 'Approvato - Seleziona Agenti', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'proposed_to_agents': { label: 'Proposto ad Agenti', color: 'bg-blue-100 text-blue-800', icon: Send },
      'proposals_received': { label: 'Proposte Ricevute', color: 'bg-blue-100 text-blue-800', icon: Users },
      'mandate_pending': { label: 'Mandato in Attesa', color: 'bg-purple-100 text-purple-800', icon: PenTool },
      'mandate_signed': { label: 'Mandato Firmato', color: 'bg-indigo-100 text-indigo-800', icon: Check },
      'auction_preparation': { label: 'In Preparazione Gara', color: 'bg-orange-100 text-orange-800', icon: Clock },
      'auction_published': { label: 'Gara Pubblicata', color: 'bg-[#0071e3]/20 text-[#005BB5]', icon: Building },
      'sold': { label: 'Venduto', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle }
    };
    return statusMap[status] || { label: status || 'Bozza', color: 'bg-gray-100 text-gray-800', icon: FileText };
  };

  const getDocumentCompletion = () => {
    const docs = property?.documents || {};
    const required = ['identity_document', 'planimetria_catastale', 'visura_catastale', 'atto_provenienza', 'photos'];
    let uploaded = 0;
    const missing = [];
    const completed = [];

    required.forEach(doc => {
      const docData = docs[doc];
      if (docData && (Array.isArray(docData) ? docData.length > 0 : docData.url || docData)) {
        uploaded++;
        completed.push(doc);
      } else {
        missing.push(doc);
      }
    });

    return {
      percentage: Math.round((uploaded / required.length) * 100),
      uploaded,
      total: required.length,
      missing,
      completed
    };
  };

  const getDocumentLabel = (key) => {
    const labels = {
      'identity_document': 'Documento di Identità',
      'planimetria_catastale': 'Planimetria Catastale',
      'visura_catastale': 'Visura Catastale',
      'atto_provenienza': 'Atto di Provenienza',
      'certificato_ape': 'Certificato APE',
      'urbanistica': 'Documenti Urbanistici',
      'photos': 'Foto Immobile'
    };
    return labels[key] || key;
  };

  const handlePhotoUpload = async (files) => {
    const fileArray = Array.from(files);
    
    // Validate files
    for (const file of fileArray) {
      const validation = await validateFile(file, 'photos');
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
    }

    setUploadingPhotos(true);
    setPhotoUploadProgress(0);

    try {
      const uploadedUrls = [];
      const folder = `sellers/${propertyId}/photos`;

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const result = await uploadToCloud(
          file,
          folder,
          'property_photo',
          (progress) => {
            const totalProgress = Math.round(((i + progress / 100) / fileArray.length) * 100);
            setPhotoUploadProgress(totalProgress);
          }
        );

        if (result.success) {
          uploadedUrls.push({
            url: result.url,
            public_id: result.public_id,
            filename: file.name,
            uploaded_at: new Date().toISOString()
          });
        }
      }

      // Update property photos
      const currentPhotos = property?.documents?.photos || [];
      const newPhotos = [...currentPhotos, ...uploadedUrls];
      
      await axios.post(`/seller/properties/${propertyId}/documents`, {
        photos: newPhotos
      });

      setProperty(prev => ({
        ...prev,
        documents: { ...prev.documents, photos: newPhotos }
      }));

      toast.success(`${uploadedUrls.length} foto caricate con successo`);
    } catch (error) {
      toast.error('Errore nel caricamento delle foto');
    } finally {
      setUploadingPhotos(false);
      setPhotoUploadProgress(0);
    }
  };

  const handleRemovePhoto = async (index) => {
    try {
      const currentPhotos = [...(property?.documents?.photos || [])];
      currentPhotos.splice(index, 1);
      
      await axios.post(`/seller/properties/${propertyId}/documents`, {
        photos: currentPhotos
      });

      setProperty(prev => ({
        ...prev,
        documents: { ...prev.documents, photos: currentPhotos }
      }));

      toast.success('Foto rimossa');
    } catch (error) {
      toast.error('Errore nella rimozione della foto');
    }
  };

  const handleDocumentUpload = async (docType, files) => {
    const file = files[0];
    
    const validation = await validateFile(file, docType);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setUploadingDoc(docType);
    setDocUploadProgress(0);

    try {
      const folder = `sellers/${propertyId}/documents`;
      const result = await uploadToCloud(
        file,
        folder,
        docType,
        (progress) => setDocUploadProgress(progress)
      );

      if (result.success) {
        const docData = {
          url: result.url,
          public_id: result.public_id,
          filename: file.name,
          uploaded_at: new Date().toISOString()
        };

        await axios.post(`/seller/properties/${propertyId}/documents`, {
          [docType]: docData
        });

        setProperty(prev => ({
          ...prev,
          documents: { ...prev.documents, [docType]: docData }
        }));

        toast.success('Documento caricato con successo');
      }
    } catch (error) {
      toast.error('Errore nel caricamento del documento');
    } finally {
      setUploadingDoc(null);
      setDocUploadProgress(0);
    }
  };

  const handleEditProperty = async () => {
    try {
      // Would call update endpoint - for now just show success
      toast.success('Informazioni aggiornate');
      setEditDialogOpen(false);
      fetchPropertyDetails();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleAcceptProposal = async (proposalId) => {
    setOtpSending(true);
    try {
      await axios.post(`/seller/proposals/${proposalId}/accept`);
      toast.success('OTP inviato al tuo telefono');
      setSelectedProposal(proposals.find(p => p.proposal_id === proposalId));
      setProposalDetailOpen(false);
      setSignDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'accettazione');
    } finally {
      setOtpSending(false);
    }
  };

  const handleSignMandate = async () => {
    if (otpCode.length !== 6) {
      toast.error('Inserisci il codice OTP completo');
      return;
    }

    setSigning(true);
    try {
      await axios.post('/seller/mandates/sign', {
        proposal_id: selectedProposal.proposal_id,
        otp_code: otpCode
      });
      toast.success('Mandato firmato con successo!');
      setSignDialogOpen(false);
      setOtpCode('');
      fetchPropertyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice OTP non valido');
    } finally {
      setSigning(false);
    }
  };

  // Handle viewing agency profile
  const handleViewAgencyProfile = async (agentId) => {
    if (!agentId) {
      toast.error('ID agente non valido');
      return;
    }
    
    setLoadingAgencyProfile(true);
    setAgencyProfileOpen(true);
    
    try {
      const response = await axios.get(`/agent/${agentId}/public-profile`);
      setAgencyProfileData(response.data);
    } catch (error) {
      console.error('Error fetching agency profile:', error);
      toast.error('Errore nel caricamento del profilo agenzia');
      setAgencyProfileOpen(false);
    } finally {
      setLoadingAgencyProfile(false);
    }
  };

  const buildTimeline = () => {
    const events = [];
    
    if (property?.created_at) {
      events.push({
        date: property.created_at,
        title: 'Immobile inserito',
        description: 'Hai inserito l\'immobile sulla piattaforma',
        icon: Plus,
        color: 'bg-blue-500'
      });
    }

    if (property?.documents) {
      const completion = getDocumentCompletion();
      if (completion.uploaded > 0) {
        events.push({
          date: property.updated_at || property.created_at,
          title: 'Documenti caricati',
          description: `${completion.uploaded}/${completion.total} documenti caricati`,
          icon: FileText,
          color: 'bg-indigo-500'
        });
      }
    }

    if (property?.status === 'approved' || property?.status === 'proposals_received') {
      events.push({
        date: property.approved_at || property.updated_at,
        title: 'Approvato da WEQASA',
        description: 'L\'immobile è ora visibile agli agenti',
        icon: CheckCircle,
        color: 'bg-green-500'
      });
    }

    proposals.forEach(p => {
      events.push({
        date: p.created_at,
        title: 'Proposta ricevuta',
        description: `Proposta da ${p.agent?.company_name || 'Agente'}`,
        icon: Users,
        color: 'bg-purple-500'
      });
    });

    mandates.forEach(m => {
      if (m.status === 'signed') {
        events.push({
          date: m.signed_at || m.created_at,
          title: 'Mandato firmato',
          description: 'Hai firmato il mandato di vendita',
          icon: PenTool,
          color: 'bg-[#0071e3]'
        });
      }
    });

    return events.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700">Immobile non trovato</h3>
        <Button onClick={() => navigate('/seller/immobili')} className="mt-4">
          Torna ai Miei Immobili
        </Button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(property.status);
  const completion = getDocumentCompletion();
  const photos = property?.documents?.photos || [];
  const timeline = buildTimeline();
  const activeMandate = mandates.find(m => m.status === 'signed' || m.status === 'pending_signature');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <button 
            onClick={() => navigate('/seller/immobili')}
            className="text-sm text-[#86868B] hover:text-[#0071e3] mb-2 flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Torna ai Miei Immobili
          </button>
          <h1 className="text-2xl font-semibold text-[#1D1D1F] ">
            {property.address}
          </h1>
          <p className="text-[#86868B]">{property.city} ({property.province}), {property.postal_code}</p>
        </div>
        <Badge className={`${statusInfo.color} px-4 py-2`}>
          <statusInfo.icon className="h-4 w-4 mr-2" />
          {statusInfo.label}
        </Badge>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-1 flex flex-wrap gap-1">
        {[
          { id: 'summary', label: 'Riepilogo', icon: Home },
          { id: 'photos', label: 'Foto', icon: Camera },
          { id: 'documents', label: 'Documenti', icon: FileText },
          { id: 'status', label: 'Stato', icon: Activity },
          { id: 'proposals', label: 'Proposte', icon: Users },
          { id: 'mandate', label: 'Mandato', icon: PenTool },
          // Chat tab - only show if mandate is signed
          ...(property?.status === 'mandate_signed' || mandates.some(m => m.property_id === propertyId) 
            ? [{ id: 'chat', label: 'Chat Agente', icon: MessageSquare }] 
            : []),
          { id: 'timeline', label: 'Cronologia', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === tab.id 
                ? 'bg-[#0071e3] text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Section 1: Property Summary */}
      {activeSection === 'summary' && (
        <div className="space-y-6">
          {/* Photo Gallery Preview */}
          {photos.length > 0 && (
            <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
              <div className="grid grid-cols-4 gap-1">
                {photos.slice(0, 4).map((photo, idx) => {
                  const isDataUrl = photo.url?.startsWith('data:');
                  const isSimulated = photo.url?.includes('storage.weqasa.it') || photo.url?.startsWith('local://');
                  const showImage = isDataUrl || (!isSimulated);
                  
                  return (
                    <div 
                      key={idx} 
                      className={`aspect-video bg-gray-100 ${idx === 0 ? 'col-span-2 row-span-2' : ''}`}
                    >
                      {showImage ? (
                        <div 
                          className="w-full h-full bg-cover bg-center"
                          style={{ backgroundImage: `url(${photo.url})` }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Camera className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {photos.length > 4 && (
                <div className="p-3 text-center border-t">
                  <button 
                    onClick={() => setActiveSection('photos')}
                    className="text-sm text-[#0071e3] font-medium hover:underline"
                  >
                    Vedi tutte le {photos.length} foto
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Property Details */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#1D1D1F]">Informazioni Immobile</h2>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditDialogOpen(true)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifica
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <span className="text-sm text-[#86868B]">Indirizzo</span>
                <p className="font-medium text-[#1D1D1F]">{property.address}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Città</span>
                <p className="font-medium text-[#1D1D1F]">{property.city}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Provincia</span>
                <p className="font-medium text-[#1D1D1F]">{property.province}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">CAP</span>
                <p className="font-medium text-[#1D1D1F]">{property.postal_code}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Tipologia</span>
                <p className="font-medium text-[#1D1D1F] capitalize">{property.property_type}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Superficie</span>
                <p className="font-medium text-[#1D1D1F]">{property.sqm} m²</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Locali</span>
                <p className="font-medium text-[#1D1D1F]">{property.rooms}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Piano</span>
                <p className="font-medium text-[#1D1D1F]">{property.floor || 'N/D'}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Condizione</span>
                <p className="font-medium text-[#1D1D1F] capitalize">{property.condition || 'Buono'}</p>
              </div>
              <div>
                <span className="text-sm text-[#86868B]">Classe Energetica</span>
                <p className="font-medium text-[#1D1D1F]">{property.energy_class || 'N/D'}</p>
              </div>
            </div>

            {property.description && (
              <div className="mt-6 pt-6 border-t">
                <span className="text-sm text-[#86868B]">Descrizione</span>
                <p className="text-gray-700 mt-1">{property.description}</p>
              </div>
            )}
          </div>

          {/* Document Completion Card */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#1D1D1F]">Completezza Documentazione</h3>
              <span className="text-2xl font-bold text-[#0071e3]">{completion.percentage}%</span>
            </div>
            <Progress value={completion.percentage} className="h-3 mb-4" />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#86868B] mb-2">Documenti Caricati</p>
                {completion.completed.map(doc => (
                  <div key={doc} className="flex items-center text-sm text-green-600 mb-1">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {getDocumentLabel(doc)}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-sm text-[#86868B] mb-2">Documenti Mancanti</p>
                {completion.missing.map(doc => (
                  <div key={doc} className="flex items-center text-sm text-orange-600 mb-1">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {getDocumentLabel(doc)}
                  </div>
                ))}
              </div>
            </div>
            
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setActiveSection('documents')}
            >
              Gestisci Documenti
            </Button>
          </div>
        </div>
      )}

      {/* Section 2: Photo Management */}
      {activeSection === 'photos' && (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-[#1D1D1F]">Gestione Foto</h2>
              <p className="text-sm text-[#86868B]">{photos.length} foto caricate</p>
            </div>
            <div>
              <input
                type="file"
                id="photo-upload"
                accept=".jpg,.jpeg,.png"
                multiple
                className="hidden"
                onChange={(e) => handlePhotoUpload(e.target.files)}
                disabled={uploadingPhotos}
              />
              <label 
                htmlFor="photo-upload"
                className={`inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 cursor-pointer bg-[#0071e3] text-white hover:bg-[#0077ED] ${uploadingPhotos ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {uploadingPhotos ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Caricamento...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> Aggiungi Foto</>
                )}
              </label>
            </div>
          </div>

          {/* Photo Requirements */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Camera className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Requisiti Foto</p>
                <ul className="space-y-1">
                  <li>• Risoluzione minima: 1920px di larghezza</li>
                  <li>• <strong>Minimo 15 foto</strong> (almeno una per ambiente)</li>
                  <li>• Dimensione massima: 20 MB per foto</li>
                  <li>• Formati accettati: JPG, PNG</li>
                </ul>
              </div>
            </div>
          </div>

          {uploadingPhotos && (
            <div className="mb-6">
              <Progress value={photoUploadProgress} className="h-2" />
              <p className="text-sm text-[#86868B] mt-1">Caricamento in corso... {photoUploadProgress}%</p>
            </div>
          )}

          {/* Photo Grid */}
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo, idx) => (
                <PhotoThumbnail 
                  key={idx}
                  photo={photo}
                  index={idx}
                  onRemove={handleRemovePhoto}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-[#86868B] mb-4">Nessuna foto caricata</p>
              <label 
                htmlFor="photo-upload"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer"
              >
                <Upload className="h-4 w-4 mr-2" />
                Carica le prime foto
              </label>
            </div>
          )}
        </div>
      )}

      {/* Section 3: Document Management */}
      {activeSection === 'documents' && (
        <div className="space-y-6">
          {/* Progress Card */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#1D1D1F]">Completezza Documentazione Immobile</h3>
              <span className="text-2xl font-bold text-[#0071e3]">{completion.percentage}%</span>
            </div>
            <Progress value={completion.percentage} className="h-4" />
            <p className="text-sm text-[#86868B] mt-3">
              {completion.uploaded} di {completion.total} documenti obbligatori caricati.
              {completion.missing.length > 0 && ' Puoi comunque procedere con la documentazione incompleta.'}
            </p>
          </div>

          {/* Documents List */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <h3 className="font-semibold text-[#1D1D1F] mb-6">Documenti Richiesti</h3>
            
            <div className="space-y-4">
              {Object.entries(DOCUMENT_CONFIGS).filter(([key]) => key !== 'photos').map(([key, config]) => {
                const docData = property?.documents?.[key];
                const isUploaded = docData && (docData.url || (Array.isArray(docData) && docData.length > 0));
                const isUploading = uploadingDoc === key;

                return (
                  <div 
                    key={key}
                    className={`border rounded-lg p-4 transition-colors ${
                      isUploaded ? 'border-green-200 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {isUploaded ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-gray-300 mt-0.5" />
                        )}
                        <div>
                          <p className="font-medium text-[#1D1D1F]">
                            {config.label}
                            {config.required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          <p className="text-sm text-[#86868B] mt-1">{config.helpText}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Formati: {config.acceptedFormats} | Max: {config.maxSizeMB} MB
                          </p>
                          
                          {isUploaded && docData && (
                            <div className="mt-3 flex items-center space-x-4">
                              <div className="flex items-center text-sm text-green-700">
                                <File className="h-4 w-4 mr-1" />
                                {docData.filename || 'Documento caricato'}
                              </div>
                              {docData.uploaded_at && (
                                <span className="text-xs text-[#86868B]">
                                  Caricato il {formatDate(docData.uploaded_at)}
                                </span>
                              )}
                            </div>
                          )}

                          {isUploading && (
                            <div className="mt-3">
                              <Progress value={docUploadProgress} className="h-2" />
                              <span className="text-xs text-[#86868B]">Caricamento... {docUploadProgress}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {isUploaded && docData?.url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDocumentPreview(docData.url, docData.filename || config.label)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <div>
                          <input
                            type="file"
                            id={`doc-${key}`}
                            accept={config.acceptedFormats}
                            className="hidden"
                            onChange={(e) => handleDocumentUpload(key, e.target.files)}
                            disabled={isUploading}
                          />
                          <label 
                            htmlFor={`doc-${key}`}
                            className={`inline-flex items-center justify-center rounded-md text-sm font-medium h-9 px-3 py-2 cursor-pointer transition-colors ${
                              isUploading ? 'opacity-50 cursor-not-allowed' :
                              isUploaded 
                                ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground' 
                                : 'bg-[#0071e3] text-white hover:bg-[#0077ED]'
                            }`}
                          >
                            {isUploading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-1" />
                                {isUploaded ? 'Sostituisci' : 'Carica'}
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Section 4: Property Status */}
      {activeSection === 'status' && (
        <div className="space-y-6">
          {/* Current Status Card */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F] mb-6">Stato del Tuo Immobile</h2>
            
            {/* Status Progress Line */}
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute top-5 left-0 w-full h-1 bg-gray-200">
                <div 
                  className="h-full bg-[#0071e3] transition-all duration-500"
                  style={{ 
                    width: property.status === 'draft' ? '10%' :
                           property.status === 'pending_review' ? '25%' :
                           property.status === 'approved' ? '40%' :
                           property.status === 'proposed_to_agents' ? '55%' :
                           property.status === 'mandate_signed' ? '75%' :
                           property.status === 'auction_preparation' ? '85%' :
                           property.status === 'sold' ? '100%' : '25%'
                  }}
                />
              </div>
              
              {/* Status Steps */}
              <div className="flex justify-between relative">
                {[
                  { key: 'draft', label: 'Bozza', icon: FileText },
                  { key: 'pending_review', label: 'In Revisione', icon: Clock },
                  { key: 'approved', label: 'Approvato', icon: CheckCircle },
                  { key: 'proposed_to_agents', label: 'Proposto', icon: Send },
                  { key: 'mandate_signed', label: 'Mandato', icon: PenTool },
                  { key: 'sold', label: 'Venduto', icon: Check }
                ].map((step, idx) => {
                  const statusOrder = ['draft', 'pending_review', 'approved', 'proposed_to_agents', 'mandate_signed', 'auction_preparation', 'sold'];
                  const currentIdx = statusOrder.indexOf(property.status);
                  const stepIdx = statusOrder.indexOf(step.key);
                  const isCompleted = stepIdx <= currentIdx;
                  const isCurrent = property.status === step.key || 
                    (property.status === 'auction_preparation' && step.key === 'mandate_signed');
                  
                  return (
                    <div key={step.key} className="flex flex-col items-center" style={{ width: '16.66%' }}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${
                        isCurrent ? 'bg-[#0071e3] text-white ring-4 ring-[#0071e3]/20' :
                        isCompleted ? 'bg-[#0071e3] text-white' : 
                        'bg-gray-200 text-[#86868B]'
                      }`}>
                        <step.icon className="h-5 w-5" />
                      </div>
                      <span className={`text-xs mt-2 text-center ${
                        isCurrent ? 'font-medium text-[#0071e3]' :
                        isCompleted ? 'text-[#1D1D1F]' : 'text-gray-400'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Current Status Detail */}
          <div className={`rounded-lg border p-6 ${
            property.status === 'approved' ? 'bg-green-50 border-green-200' :
            property.status === 'proposed_to_agents' ? 'bg-blue-50 border-blue-200' :
            property.status === 'mandate_signed' ? 'bg-indigo-50 border-indigo-200' :
            property.status === 'pending_review' ? 'bg-yellow-50 border-yellow-200' :
            property.status === 'draft' ? 'bg-gray-50 border-gray-200' :
            'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start space-x-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                property.status === 'approved' ? 'bg-green-500' :
                property.status === 'mandate_signed' ? 'bg-indigo-500' :
                property.status === 'pending_review' ? 'bg-[#0071e3]' :
                'bg-gray-500'
              } text-white`}>
                <statusInfo.icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1D1D1F] text-lg">{statusInfo.label}</h3>
                <p className="text-gray-600 mt-1">
                  {property.status === 'draft' && 
                    'Il tuo immobile è in bozza. Completa la documentazione e invialo per la revisione.'}
                  {property.status === 'pending_review' && 
                    'Il tuo immobile è in fase di revisione da parte del team WEQASA. Riceverai una notifica quando sarà approvato.'}
                  {property.status === 'approved' && 
                    'Il tuo immobile è stato approvato! Seleziona le agenzie immobiliari a cui proporre il tuo immobile.'}
                  {property.status === 'proposed_to_agents' && 
                    'Hai proposto il tuo immobile alle agenzie selezionate. Attendi le loro risposte con la valutazione.'}
                  {(property.status === 'mandate_signed' || property.status === 'auction_preparation') && 
                    'Hai firmato il mandato con un agente. L\'agente sta preparando la gara di vendita.'}
                  {property.status === 'auction_published' && 
                    'La gara di vendita è stata pubblicata e gli acquirenti possono partecipare.'}
                  {property.status === 'sold' && 
                    'Congratulazioni! Il tuo immobile è stato venduto con successo.'}
                </p>
              </div>
            </div>
          </div>

          {/* Actions Based on Status */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <h3 className="font-semibold text-[#1D1D1F] mb-4">Azioni Disponibili</h3>
            <div className="space-y-3">
              {property.status === 'draft' && (
                <>
                  <Button 
                    onClick={() => setActiveSection('documents')}
                    className="w-full justify-start btn-primary"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Completa la documentazione
                  </Button>
                  <Button 
                    onClick={() => setEditDialogOpen(true)}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Modifica informazioni immobile
                  </Button>
                </>
              )}
              {property.status === 'pending_review' && (
                <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg">
                  <Loader2 className="h-5 w-5 animate-spin text-[#0071e3]" />
                  <span className="text-[#1D1D1F]">In attesa di approvazione da parte di WEQASA...</span>
                </div>
              )}
              {property.status === 'approved' && (
                <AgentSelectionForApprovedProperty 
                  propertyId={property.property_id} 
                  onProposalsSent={() => {
                    fetchPropertyDetails();
                    toast.success('Proposte inviate agli agenti selezionati!');
                  }}
                />
              )}
              {property.status === 'proposed_to_agents' && (
                <ProposedAgentsSection 
                  property={property}
                  proposals={proposals}
                  onRefresh={fetchPropertyDetails}
                  onViewProposals={() => setActiveSection('proposals')}
                />
              )}
              {(property.status === 'mandate_signed' || property.status === 'auction_preparation') && (
                <>
                  <Button 
                    onClick={() => setActiveSection('mandate')}
                    className="w-full justify-start btn-primary"
                  >
                    <PenTool className="h-4 w-4 mr-2" />
                    Visualizza mandato firmato
                  </Button>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Agente incaricato:</strong> {activeMandate?.agent?.company_name || 'N/D'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Completion Checklist */}
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
            <h3 className="font-semibold text-[#1D1D1F] mb-4">Checklist Completamento</h3>
            <div className="space-y-3">
              {[
                { 
                  label: 'Dati immobile inseriti', 
                  completed: true, 
                  action: () => setActiveSection('summary')
                },
                { 
                  label: `Foto caricate (${photos.length}/5 minimo)`, 
                  completed: photos.length >= 5,
                  action: () => setActiveSection('photos')
                },
                { 
                  label: `Documenti completati (${completion.percentage}%)`, 
                  completed: completion.percentage >= 80,
                  action: () => setActiveSection('documents')
                },
                { 
                  label: 'Immobile approvato da WEQASA', 
                  completed: ['approved', 'mandate_signed', 'auction_preparation', 'sold'].includes(property.status),
                  action: null
                },
                { 
                  label: 'Proposta agente accettata', 
                  completed: ['mandate_signed', 'auction_preparation', 'sold'].includes(property.status),
                  action: () => setActiveSection('proposals')
                },
                { 
                  label: 'Mandato firmato', 
                  completed: ['mandate_signed', 'auction_preparation', 'sold'].includes(property.status),
                  action: () => setActiveSection('mandate')
                }
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    item.completed ? 'bg-green-50' : 'bg-gray-50'
                  } ${item.action ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                  onClick={item.action}
                >
                  <div className="flex items-center space-x-3">
                    {item.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    )}
                    <span className={item.completed ? 'text-green-800' : 'text-gray-600'}>
                      {item.label}
                    </span>
                  </div>
                  {item.action && !item.completed && (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Section 5: Agent Proposals */}
      {activeSection === 'proposals' && (
        <div className="space-y-6">
          {property.status === 'approved' || property.status === 'proposals_received' || proposals.length > 0 ? (
            <>
              {/* Header with comparison toggle */}
              <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-[#1D1D1F] mb-2">Proposte degli Agenti</h2>
                    <p className="text-sm text-[#86868B]">
                      {proposals.length > 0 
                        ? `Hai ricevuto ${proposals.length} proposta/e. Confrontale e scegli l'agente migliore per te.`
                        : 'Non hai ancora ricevuto proposte. Gli agenti della tua zona sono stati notificati.'}
                    </p>
                  </div>
                  {proposals.length > 1 && (
                    <Button
                      variant={comparisonView ? "default" : "outline"}
                      onClick={() => setComparisonView(!comparisonView)}
                      className={comparisonView ? "bg-[#0071e3] hover:bg-[#0077ED]" : ""}
                    >
                      <Scale className="h-4 w-4 mr-2" />
                      {comparisonView ? 'Vista Card' : 'Confronta'}
                    </Button>
                  )}
                </div>
              </div>

              {proposals.length > 0 ? (
                <>
                  {/* Comparison Table View */}
                  {comparisonView && proposals.length > 1 ? (
                    <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
                      <div className="p-4 bg-[#0071e3]/5 border-b">
                        <div className="flex items-center space-x-2">
                          <Table2 className="h-5 w-5 text-[#0071e3]" />
                          <h3 className="font-semibold text-[#1D1D1F]">Confronta Proposte Agenti</h3>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Posizione</th>
                              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Agente</th>
                              <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Valore Stimato</th>
                              <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Prezzo Base Gara</th>
                              <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Prezzo Minimo</th>
                              <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Azione</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {proposals.map((proposal, idx) => {
                              const isHighestValue = proposal.estimated_value === Math.max(...proposals.map(p => p.estimated_value));
                              const isTopRanked = proposal.is_top_ranked || (proposal.ranking_position === 1 && proposal.ranking?.total_score > 50);
                              const isVerified = proposal.agent?.verification_status === 'approved';
                              const rankingBreakdown = proposal.ranking?.breakdown || {};
                              const isNearby = rankingBreakdown.geographical_proximity?.score >= 0.7;
                              
                              return (
                                <tr key={proposal.proposal_id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${isTopRanked ? 'ring-2 ring-[#0071e3] ring-inset' : ''}`}>
                                  {/* Ranking Position */}
                                  <td className="px-4 py-4">
                                    <div className="flex flex-col items-start space-y-1">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                        proposal.ranking_position === 1 ? 'bg-[#0071e3] text-white' : 
                                        proposal.ranking_position === 2 ? 'bg-gray-300 text-gray-800' :
                                        proposal.ranking_position === 3 ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'
                                      }`}>
                                        #{proposal.ranking_position || idx + 1}
                                      </div>
                                      {proposal.ranking?.total_score && (
                                        <span className="text-xs text-[#86868B]">{Math.round(proposal.ranking.total_score)}/100</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-10 h-10 bg-[#0071e3] text-white rounded-full flex items-center justify-center font-semibold">
                                        {(proposal.agent?.company_name || 'A')[0]}
                                      </div>
                                      <div>
                                        <div className="flex items-center space-x-2">
                                          <p className="font-medium text-[#1D1D1F]">{proposal.agent?.company_name || 'Agenzia'}</p>
                                          {isVerified && (
                                            <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded flex items-center">
                                              <Check className="h-3 w-3 mr-0.5" />
                                              Verificato
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-[#86868B]">{proposal.agent?.first_name} {proposal.agent?.last_name}</p>
                                        {/* Ranking Badges */}
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {isTopRanked && (
                                            <span className="bg-[#0071e3]/20 text-[#0071e3] text-xs px-1.5 py-0.5 rounded font-medium">
                                              Consigliato
                                            </span>
                                          )}
                                          {isNearby && (
                                            <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded">
                                              Vicino a te
                                            </span>
                                          )}
                                          {proposal.agent_performance?.mandates_won > 0 && (
                                            <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded">
                                              {proposal.agent_performance.mandates_won} mandati
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right">
                                    <div className="flex items-center justify-end space-x-2">
                                      <span className="font-semibold text-[#1D1D1F]">{formatPrice(proposal.estimated_value)}</span>
                                      {isHighestValue && (
                                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">Top</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right font-semibold text-[#0071e3]">
                                    {formatPrice(proposal.auction_start_price)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-gray-700">
                                    {formatPrice(proposal.reserve_price)}
                                  </td>
                                  <td className="px-4 py-4 text-center">
                                    <div className="flex items-center justify-center space-x-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewAgencyProfile(proposal.agent_id)}
                                        title="Visualizza profilo agenzia"
                                      >
                                        <Briefcase className="h-4 w-4" />
                                      </Button>
                                      {proposal.status === 'pending' ? (
                                        <Button
                                          size="sm"
                                          className="bg-[#0071e3] hover:bg-[#005BB5]"
                                          onClick={() => {
                                            setSelectedProposal(proposal);
                                            setConfirmAgentDialogOpen(true);
                                          }}
                                        >
                                          Seleziona
                                        </Button>
                                      ) : (
                                        <Badge className={proposal.status === 'accepted' ? 'badge-active' : 'badge-closed'}>
                                          {proposal.status === 'accepted' ? 'Accettata' : 'Rifiutata'}
                                        </Badge>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="p-4 bg-gradient-to-r from-[#0071e3]/10 to-[#0071e3]/10 border-t">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-700 flex items-center">
                            <Star className="h-4 w-4 mr-2 text-[#0071e3]" />
                            Gli agenti sono ordinati in base al nostro algoritmo di ranking che considera: abbonamento attivo, profilo verificato, prossimità geografica, completezza proposta e velocità di risposta.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Card View */
                    <div className="grid md:grid-cols-2 gap-6">
                      {proposals.map((proposal) => {
                        const isTopRanked = proposal.is_top_ranked || (proposal.ranking_position === 1 && proposal.ranking?.total_score > 50);
                        const isVerified = proposal.agent?.verification_status === 'approved';
                        const rankingBreakdown = proposal.ranking?.breakdown || {};
                        const isNearby = rankingBreakdown.geographical_proximity?.score >= 0.7;
                        
                        return (
                        <div key={proposal.proposal_id} className={`bg-white rounded-lg border ${isTopRanked ? 'border-[#0071e3] ring-2 ring-[#0071e3]/20' : 'border-gray-100'} p-6 hover:shadow-lg transition-shadow relative`}>
                          {/* Ranking Badge */}
                          {proposal.ranking_position && (
                            <div className={`absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-lg ${
                              proposal.ranking_position === 1 ? 'bg-[#0071e3] text-white' : 
                              proposal.ranking_position === 2 ? 'bg-gray-300 text-gray-800' :
                              proposal.ranking_position === 3 ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                              #{proposal.ranking_position}
                            </div>
                          )}
                          
                          {/* Recommended Badge */}
                          {isTopRanked && (
                            <div className="absolute -top-3 right-4 bg-[#0071e3] text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg flex items-center">
                              <Star className="h-3 w-3 mr-1" />
                              Consigliato da WEQASA
                            </div>
                          )}
                          
                          <div className="flex items-start justify-between mb-4 mt-2">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-[#0071e3] text-white rounded-full flex items-center justify-center font-bold text-lg">
                                {(proposal.agent?.company_name || 'A')[0]}
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h3 className="font-semibold text-[#1D1D1F]">
                                    {proposal.agent?.company_name || 'Agenzia Immobiliare'}
                                  </h3>
                                  {isVerified && (
                                    <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded flex items-center">
                                      <Check className="h-3 w-3 mr-0.5" />
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-[#86868B]">
                                  {proposal.agent?.first_name} {proposal.agent?.last_name}
                                </p>
                                {/* Ranking Badges */}
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {isVerified && (
                                    <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded">
                                      Profilo verificato
                                    </span>
                                  )}
                                  {isNearby && (
                                    <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded">
                                      Più vicino a te
                                    </span>
                                  )}
                                  {proposal.agent_performance?.mandates_won > 0 && (
                                    <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded">
                                      {proposal.agent_performance.mandates_won} mandati vinti
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Badge className={
                              proposal.status === 'accepted' ? 'badge-active' :
                              proposal.status === 'rejected' ? 'badge-closed' :
                              'badge-pending'
                            }>
                              {proposal.status === 'accepted' ? 'Accettata' :
                               proposal.status === 'rejected' ? 'Rifiutata' : 'In Attesa'}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-[#86868B]">Valore Stimato</p>
                              <p className="text-lg font-semibold text-[#1D1D1F]">{formatPrice(proposal.estimated_value)}</p>
                            </div>
                            <div className="bg-[#0071e3]/10 rounded-lg p-3">
                              <p className="text-xs text-[#86868B]">Prezzo Base Gara</p>
                              <p className="text-lg font-bold text-[#0071e3]">{formatPrice(proposal.auction_start_price)}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs text-[#86868B]">Prezzo Minimo</p>
                              <p className="text-lg font-semibold text-[#1D1D1F]">{formatPrice(proposal.reserve_price)}</p>
                            </div>
                            <div className="bg-[#0071e3]/10 rounded-lg p-3">
                              <p className="text-xs text-[#86868B]">Commissione Agente</p>
                              <p className="text-lg font-bold text-[#0071e3]">{proposal.commission_percentage || 2}%</p>
                            </div>
                          </div>

                          {proposal.message && (
                            <div className="bg-blue-50 rounded-lg p-3 mb-4">
                              <p className="text-sm text-blue-800">{proposal.message}</p>
                            </div>
                          )}

                          <div className="flex space-x-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                setSelectedProposal(proposal);
                                setProposalDetailOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Dettagli
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => handleViewAgencyProfile(proposal.agent_id)}
                            >
                              <Briefcase className="h-4 w-4 mr-2" />
                              Profilo Agenzia
                            </Button>
                          </div>
                          
                          <div className="mt-3">
                            {proposal.status === 'pending' && (
                              <Button
                                className="w-full bg-[#0071e3] hover:bg-[#005BB5]"
                                onClick={() => {
                                  setSelectedProposal(proposal);
                                  setConfirmAgentDialogOpen(true);
                                }}
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Seleziona Agente
                              </Button>
                            )}
                          </div>
                          
                          {/* Ranking Score (subtle) */}
                          {proposal.ranking?.total_score && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <div className="flex items-center justify-between text-xs text-[#86868B]">
                                <span>Punteggio ranking</span>
                                <div className="flex items-center">
                                  <div className="w-20 h-1.5 bg-gray-200 rounded-full mr-2">
                                    <div 
                                      className="h-full bg-[#0071e3] rounded-full" 
                                      style={{width: `${proposal.ranking.total_score}%`}}
                                    />
                                  </div>
                                  <span>{Math.round(proposal.ranking.total_score)}/100</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )})}
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
                  <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">In attesa di proposte</h3>
                  <p className="text-[#86868B]">Gli agenti immobiliari della tua zona stanno valutando il tuo immobile.</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <AlertCircle className="h-6 w-6 text-[#0071e3] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#1D1D1F]">Immobile in revisione</h3>
                  <p className="text-sm text-[#86868B] mt-1">
                    Il tuo immobile è ancora in fase di revisione da parte di WEQASA. 
                    Una volta approvato, sarà visibile agli agenti che potranno inviarti le loro proposte.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Agent Selection Confirmation Dialog */}
      <Dialog open={confirmAgentDialogOpen} onOpenChange={setConfirmAgentDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Award className="h-5 w-5 text-[#0071e3]" />
              <span>Conferma Selezione Agente</span>
            </DialogTitle>
            <DialogDescription>
              Seleziona l'agente immobiliare che gestirà la vendita del tuo immobile.
            </DialogDescription>
          </DialogHeader>
          
          {selectedProposal && (
            <div className="space-y-4">
              <div className="bg-[#0071e3]/5 rounded-lg p-4">
                <p className="text-gray-700">
                  Stai per selezionare <strong>{selectedProposal.agent?.company_name}</strong> per la vendita del tuo immobile tramite WEQASA.
                </p>
              </div>

              <div className="border rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-[#1D1D1F]">Riepilogo Proposta</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[#86868B]">Valore Stimato</p>
                    <p className="font-semibold">{formatPrice(selectedProposal.estimated_value)}</p>
                  </div>
                  <div>
                    <p className="text-[#86868B]">Prezzo Base Gara</p>
                    <p className="font-semibold text-[#0071e3]">{formatPrice(selectedProposal.auction_start_price)}</p>
                  </div>
                  <div>
                    <p className="text-[#86868B]">Prezzo Minimo</p>
                    <p className="font-semibold">{formatPrice(selectedProposal.reserve_price)}</p>
                  </div>
                  <div>
                    <p className="text-[#86868B]">Commissione</p>
                    <p className="font-semibold text-[#0071e3]">{selectedProposal.commission_percentage || 2}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-[#1D1D1F]">
                  <strong>Attenzione:</strong> Dopo la conferma, dovrai firmare digitalmente il mandato di vendita tramite codice OTP.
                </p>
              </div>

              <div className="flex space-x-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setConfirmAgentDialogOpen(false)}
                >
                  Annulla
                </Button>
                <Button 
                  className="flex-1 bg-[#0071e3] hover:bg-[#005BB5]"
                  onClick={() => {
                    setConfirmAgentDialogOpen(false);
                    handleAcceptProposal(selectedProposal.proposal_id);
                  }}
                  disabled={otpSending}
                >
                  {otpSending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Conferma e Procedi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Section 6: Mandate Status */}
      {activeSection === 'mandate' && (
        <div className="space-y-6">
          {activeMandate ? (
            <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[#1D1D1F]">Mandato di Vendita</h2>
                <Badge className={
                  activeMandate.status === 'signed' ? 'bg-green-100 text-green-800' :
                  'bg-yellow-100 text-[#1D1D1F]'
                }>
                  {activeMandate.status === 'signed' ? 'Firmato Digitalmente' : 'In Attesa Firma'}
                </Badge>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-medium text-[#1D1D1F] mb-4">Riepilogo Mandato</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-[#86868B]">Agente</p>
                    <p className="font-medium">{activeMandate.agent?.company_name || 'N/D'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#86868B]">Valore Stimato</p>
                    <p className="font-medium">{formatPrice(activeMandate.terms?.estimated_value)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#86868B]">Prezzo Base Gara</p>
                    <p className="font-medium text-[#0071e3]">{formatPrice(activeMandate.terms?.auction_start_price)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#86868B]">Commissione</p>
                    <p className="font-medium">{activeMandate.terms?.commission_percentage || 2}%</p>
                  </div>
                </div>
                
                {activeMandate.signed_at && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-[#86868B]">Data Firma</p>
                    <p className="font-medium">{formatDate(activeMandate.signed_at)}</p>
                  </div>
                )}
              </div>

              {activeMandate.status !== 'signed' && (
                <Button
                  className="w-full bg-[#0071e3] hover:bg-[#005BB5]"
                  onClick={() => setSignDialogOpen(true)}
                >
                  <PenTool className="h-4 w-4 mr-2" />
                  Firma Mandato
                </Button>
              )}

              {activeMandate.status === 'signed' && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Mandato Attivo</p>
                        <p className="text-sm text-green-700">
                          L'agente può ora procedere con la preparazione della gara di vendita.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`/mandates/${activeMandate.mandate_id}/pdf`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `mandato_weqasa_${activeMandate.mandate_id}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success('Mandato scaricato con successo');
                      } catch (error) {
                        toast.error('Errore nel download del mandato');
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Scarica Mandato PDF
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
              <PenTool className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessun mandato attivo</h3>
              <p className="text-[#86868B] mb-4">
                Per firmare un mandato, devi prima accettare una proposta da un agente.
              </p>
              <Button onClick={() => setActiveSection('proposals')}>
                Vai alle Proposte
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Section 7: Timeline */}
      {activeSection === 'timeline' && (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
          <h2 className="text-lg font-semibold text-[#1D1D1F] mb-6">Cronologia Eventi</h2>
          
          {timeline.length > 0 ? (
            <div className="space-y-6">
              {timeline.map((event, idx) => (
                <div key={idx} className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-full ${event.color} flex items-center justify-center flex-shrink-0`}>
                    <event.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 pb-6 border-b border-gray-100 last:border-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-[#1D1D1F]">{event.title}</h4>
                      <span className="text-sm text-[#86868B]">{formatDate(event.date)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-[#86868B]">Nessun evento registrato</p>
            </div>
          )}
        </div>
      )}

      {/* Section 8: Chat with Agent */}
      {activeSection === 'chat' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-[#0071e3] to-[#1A6B7C] rounded-lg p-4 text-white">
            <div className="flex items-center space-x-3">
              <MessageSquare className="h-6 w-6" />
              <div>
                <h2 className="font-semibold">Comunicazione con l'Agente</h2>
                <p className="text-sm text-white/80">
                  Coordina la preparazione dell'immobile per la gara di vendita
                </p>
              </div>
            </div>
          </div>
          
          <PropertyChat 
            propertyId={propertyId}
            userType="seller"
            compact={false}
          />
          
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Suggerimento</p>
                <p>
                  Usa questa chat per inviare documenti mancanti richiesti dall'agente. 
                  Clicca sull'icona della graffetta per caricare file PDF o immagini.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Property Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Informazioni Immobile</DialogTitle>
            <DialogDescription>
              Aggiorna i dati del tuo immobile. Le modifiche saranno salvate immediatamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Indirizzo</Label>
                <Input
                  value={editForm.address}
                  onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                />
              </div>
              <div>
                <Label>Città</Label>
                <Input
                  value={editForm.city}
                  onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                />
              </div>
              <div>
                <Label>Provincia</Label>
                <Input
                  value={editForm.province}
                  onChange={(e) => setEditForm({...editForm, province: e.target.value})}
                  maxLength={2}
                />
              </div>
              <div>
                <Label>Superficie (m²)</Label>
                <Input
                  type="number"
                  value={editForm.sqm}
                  onChange={(e) => setEditForm({...editForm, sqm: e.target.value})}
                />
              </div>
              <div>
                <Label>Locali</Label>
                <Input
                  type="number"
                  value={editForm.rooms}
                  onChange={(e) => setEditForm({...editForm, rooms: e.target.value})}
                />
              </div>
              <div>
                <Label>Condizione</Label>
                <Select 
                  value={editForm.condition} 
                  onValueChange={(v) => setEditForm({...editForm, condition: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nuovo">Nuovo</SelectItem>
                    <SelectItem value="ottimo">Ottimo</SelectItem>
                    <SelectItem value="buono">Buono</SelectItem>
                    <SelectItem value="da_ristrutturare">Da Ristrutturare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Classe Energetica</Label>
                <Select 
                  value={editForm.energy_class} 
                  onValueChange={(v) => setEditForm({...editForm, energy_class: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['A4', 'A3', 'A2', 'A1', 'B', 'C', 'D', 'E', 'F', 'G'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleEditProperty} className="bg-[#0071e3] hover:bg-[#0077ED]">
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proposal Detail Dialog */}
      <Dialog open={proposalDetailOpen} onOpenChange={setProposalDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettaglio Proposta</DialogTitle>
            <DialogDescription>
              Visualizza tutti i dettagli della proposta ricevuta dall'agente.
            </DialogDescription>
          </DialogHeader>
          {selectedProposal && (
            <div className="space-y-6 py-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-[#1D1D1F] mb-2">
                  {selectedProposal.agent?.company_name || 'Agenzia Immobiliare'}
                </h4>
                <p className="text-sm text-gray-600">
                  {selectedProposal.agent?.first_name} {selectedProposal.agent?.last_name}
                </p>
                {selectedProposal.agent?.phone && (
                  <p className="text-sm text-gray-600">{selectedProposal.agent?.phone}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#86868B]">Valore Stimato</p>
                  <p className="text-xl font-semibold text-[#1D1D1F]">{formatPrice(selectedProposal.estimated_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Prezzo Base Gara</p>
                  <p className="text-xl font-bold text-[#0071e3]">{formatPrice(selectedProposal.auction_start_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Prezzo Minimo</p>
                  <p className="text-xl font-semibold text-[#1D1D1F]">{formatPrice(selectedProposal.reserve_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#86868B]">Commissione</p>
                  <p className="text-xl font-bold text-[#0071e3]">{selectedProposal.commission_percentage || 2}%</p>
                </div>
              </div>

              {selectedProposal.message && (
                <div>
                  <p className="text-sm text-[#86868B] mb-2">Messaggio dell'Agente</p>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-blue-800">{selectedProposal.message}</p>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-[#1D1D1F]">
                  Accettando questa proposta, procederai alla firma digitale del mandato di vendita. 
                  L'agente potrà quindi gestire la gara di vendita del tuo immobile.
                </p>
              </div>

              {selectedProposal.status === 'pending' && (
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setProposalDetailOpen(false)}
                  >
                    Chiudi
                  </Button>
                  <Button
                    className="flex-1 bg-[#0071e3] hover:bg-[#005BB5]"
                    onClick={() => handleAcceptProposal(selectedProposal.proposal_id)}
                    disabled={otpSending}
                  >
                    {otpSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <><Check className="h-4 w-4 mr-2" /> Accetta e Firma Mandato</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Agency Profile Dialog */}
      <Dialog open={agencyProfileOpen} onOpenChange={setAgencyProfileOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agency-profile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Briefcase className="h-5 w-5 text-[#0071e3]" />
              <span>Profilo Agenzia</span>
            </DialogTitle>
            <DialogDescription>
              Visualizza il profilo completo dell'agenzia immobiliare
            </DialogDescription>
          </DialogHeader>
          
          {loadingAgencyProfile ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
            </div>
          ) : agencyProfileData ? (
            <div className="py-4">
              <AgencyProfileCard 
                profile={agencyProfileData.profile}
                agent={agencyProfileData.agent}
                weqasaScore={agencyProfileData.weqasa_score}
                performance={agencyProfileData.performance}
                isPreview={false}
              />
              
              {/* Extended Description */}
              {agencyProfileData.profile?.description_long && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-[#1D1D1F] mb-2">Descrizione Completa</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-line">
                    {agencyProfileData.profile.description_long}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-[#86868B]">
              Impossibile caricare il profilo
            </div>
          )}
          
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setAgencyProfileOpen(false)}>
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign Mandate Dialog */}
      <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <PenTool className="h-5 w-5 text-[#0071e3]" />
              <span>Firma Digitale Mandato</span>
            </DialogTitle>
            <DialogDescription>
              Inserisci il codice OTP per completare la firma digitale del mandato di vendita.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Abbiamo inviato un codice OTP al tuo telefono. Inseriscilo per confermare la firma del mandato.
              </p>
              
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={setOtpCode}
              >
                <InputOTPGroup className="justify-center">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-[#1D1D1F]">
                Confermando, firmerai digitalmente il mandato di vendita con {selectedProposal?.agent?.company_name || 'l\'agente selezionato'}.
              </p>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setSignDialogOpen(false);
                  setOtpCode('');
                }}
              >
                Annulla
              </Button>
              <Button
                className="flex-1 bg-[#0071e3] hover:bg-[#005BB5]"
                onClick={handleSignMandate}
                disabled={signing || otpCode.length !== 6}
              >
                {signing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <><Check className="h-4 w-4 mr-2" /> Conferma Firma</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Seller Messages Page
const SellerMessagesPage = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    try {
      const response = await axios.get('/chat/my-chats');
      setChats(response.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'HH:mm', { locale: it });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seller-messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Messaggi</h1>
          <p className="text-[#86868B] mt-1">Comunicazioni con gli agenti immobiliari</p>
        </div>
      </div>

      {selectedChat ? (
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedChat(null)}
            className="mb-2"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Torna alla lista
          </Button>
          
          <div className="bg-gradient-to-r from-[#0071e3] to-[#1A6B7C] rounded-lg p-4 text-white">
            <div className="flex items-center space-x-3">
              <Building className="h-6 w-6" />
              <div>
                <h2 className="font-semibold">{selectedChat.property?.title || 'Immobile'}</h2>
                <p className="text-sm text-white/80">
                  Agenzia: {selectedChat.agent_name}
                </p>
              </div>
            </div>
          </div>
          
          <PropertyChat 
            propertyId={selectedChat.property_id}
            userType="seller"
            compact={false}
          />
        </div>
      ) : (
        <>
          {chats.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
              <div className="divide-y divide-gray-100">
                {chats.map((chat) => (
                  <button
                    key={chat.chat_id}
                    onClick={() => setSelectedChat(chat)}
                    className="w-full p-4 flex items-center space-x-4 hover:bg-gray-50 transition-colors text-left"
                    data-testid={`chat-${chat.chat_id}`}
                  >
                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {chat.property?.photos?.[0] ? (
                        <img 
                          src={chat.property.photos[0].url || chat.property.photos[0]} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Building className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-[#1D1D1F] truncate">
                          {chat.property?.title || 'Immobile'}
                        </h3>
                        {chat.last_message_at && (
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                            {formatTime(chat.last_message_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {chat.agent_name}
                      </p>
                      <p className="text-sm text-gray-400 truncate mt-0.5">
                        {chat.last_message_preview || 'Nessun messaggio'}
                      </p>
                    </div>
                    {chat.unread_seller > 0 && (
                      <Badge className="bg-[#0071e3] text-white flex-shrink-0">
                        {chat.unread_seller}
                      </Badge>
                    )}
                    <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
              <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna conversazione</h3>
              <p className="text-[#86868B] text-sm max-w-md mx-auto">
                Le chat verranno attivate automaticamente quando firmerai un mandato di vendita con un agente.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Security Page with 2FA
const SellerSecurityPage = () => {
  const { token } = useAuth();

  return (
    <div className="space-y-8" data-testid="seller-security-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#1D1D1F] tracking-tight mb-2">Sicurezza</h1>
        <p className="text-[#86868B]">Gestisci le impostazioni di sicurezza del tuo account</p>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="p-6 border-b border-[#E5E5EA]">
          <h2 className="text-lg font-semibold text-[#1D1D1F] flex items-center space-x-2">
            <Shield className="h-5 w-5 text-[#0071e3]" />
            <span>Autenticazione a due fattori (2FA)</span>
          </h2>
          <p className="text-sm text-[#86868B] mt-1">
            Aggiungi un ulteriore livello di sicurezza al tuo account
          </p>
        </div>
        <div className="p-6">
          <TwoFactorSetup token={token} />
        </div>
      </div>

      <Link 
        to="/seller/privacy"
        className="block bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-[#0071e3]/10 flex items-center justify-center">
              <User className="h-6 w-6 text-[#0071e3]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1D1D1F]">Privacy e dati personali</h3>
              <p className="text-sm text-[#86868B]">Gestisci i tuoi dati e le preferenze GDPR</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[#86868B]" />
        </div>
      </Link>
    </div>
  );
};

// Seller Privacy Dashboard Page
const SellerPrivacyDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    marketing_emails: false,
    analytics_tracking: true,
    third_party_sharing: false,
    personalized_content: true
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const handlePreferenceChange = async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    toast.success('Preferenza aggiornata');
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Richiesta di esportazione dati inviata. Riceverai un\'email con il link per scaricare i tuoi dati.');
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Errore nella richiesta di esportazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Richiesta di eliminazione account inviata. Sarai contattato per confermare l\'operazione.');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Errore nella richiesta di eliminazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/seller/sicurezza')} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F]">Privacy e Dati Personali</h1>
          <p className="text-[#86868B] mt-1">Gestisci i tuoi dati secondo il GDPR (Reg. UE 2016/679)</p>
        </div>
      </div>

      {/* GDPR Rights Info */}
      <div className="bg-[#0071e3]/5 border border-[#0071e3]/20 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <Shield className="h-8 w-8 text-[#0071e3] flex-shrink-0" />
          <div>
            <h2 className="font-semibold text-[#1D1D1F] mb-2">I tuoi diritti secondo il GDPR</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              In qualità di cittadino dell'Unione Europea, hai diritto di accedere, rettificare, cancellare 
              e portare i tuoi dati personali. Puoi anche opporti al trattamento e revocare il consenso in qualsiasi momento.
            </p>
          </div>
        </div>
      </div>

      {/* Data Overview */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#0071e3]" />
          Dati in nostro possesso
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati identificativi</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Nome e cognome</li>
              <li>• Indirizzo email: {user?.email}</li>
              <li>• Numero di telefono (se fornito)</li>
              <li>• Codice fiscale (se fornito)</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati immobiliari</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Immobili caricati</li>
              <li>• Documenti di proprietà</li>
              <li>• Foto e planimetrie</li>
              <li>• Storico gare di vendita</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati di utilizzo</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Log di accesso</li>
              <li>• Proposte ricevute</li>
              <li>• Mandati firmati</li>
              <li>• Cronologia messaggi</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati finanziari</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Coordinate bancarie (per accrediti)</li>
              <li>• Storico transazioni</li>
              <li>• Documenti fiscali</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Consent Preferences */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-[#0071e3]" />
          Preferenze di consenso
        </h2>
        <div className="space-y-4">
          {[
            { key: 'marketing_emails', title: 'Email di marketing', description: 'Ricevi aggiornamenti su nuove funzionalità e promozioni WEQASA' },
            { key: 'analytics_tracking', title: 'Analytics e miglioramento servizio', description: 'Ci permette di analizzare l\'uso della piattaforma per migliorare l\'esperienza' },
            { key: 'third_party_sharing', title: 'Condivisione con terze parti', description: 'Condivisione dati con partner selezionati per servizi aggiuntivi' },
            { key: 'personalized_content', title: 'Contenuti personalizzati', description: 'Mostra agenti e suggerimenti basati sulle tue preferenze' }
          ].map((pref) => (
            <div key={pref.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <div className="flex-1">
                <h3 className="font-medium text-[#1D1D1F]">{pref.title}</h3>
                <p className="text-sm text-gray-500">{pref.description}</p>
              </div>
              <button
                onClick={() => handlePreferenceChange(pref.key, !preferences[pref.key])}
                className={`relative w-12 h-6 rounded-full transition-colors ${preferences[pref.key] ? 'bg-[#0071e3]' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${preferences[pref.key] ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Data Actions */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <Download className="h-5 w-5 text-[#0071e3]" />
          Azioni sui tuoi dati
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Esporta i tuoi dati</h3>
            <p className="text-sm text-gray-500 mb-4">Scarica una copia di tutti i dati (Art. 20 GDPR)</p>
            <Button variant="outline" onClick={() => setShowExportDialog(true)}>
              <Download className="h-4 w-4 mr-2" />
              Richiedi esportazione
            </Button>
          </div>
          <div className="border border-red-200 rounded-xl p-4 bg-red-50/50">
            <h3 className="font-medium text-red-800 mb-2">Elimina account e dati</h3>
            <p className="text-sm text-red-600 mb-4">Elimina definitivamente account e dati (Art. 17 GDPR)</p>
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-100" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Richiedi eliminazione
            </Button>
          </div>
        </div>
      </div>

      {/* Legal References */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h2 className="font-semibold text-[#1D1D1F] mb-4">Riferimenti normativi</h2>
        <p className="text-sm text-gray-600">
          Per esercitare i tuoi diritti o per domande sulla privacy: <strong>privacy@weqasa.it</strong>
        </p>
        <p className="text-xs text-gray-500 mt-4">
          Titolare del trattamento: WEQASA S.r.l.
        </p>
      </div>

      {/* Dialogs */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Esporta i tuoi dati</DialogTitle>
            <DialogDescription>Riceverai un'email con un link per scaricare i tuoi dati.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Annulla</Button>
            <Button onClick={handleExportData} disabled={loading} className="btn-primary">
              {loading ? 'Elaborazione...' : 'Conferma esportazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Elimina account e dati</DialogTitle>
            <DialogDescription>Questa azione è irreversibile.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">Attenzione:</p>
              <ul className="mt-2 text-sm text-red-700 space-y-1">
                <li>• Perderai l'accesso a tutti i tuoi immobili</li>
                <li>• Le gare in corso saranno annullate</li>
                <li>• I documenti saranno eliminati</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={loading} className="bg-red-600 hover:bg-red-700">
              {loading ? 'Elaborazione...' : 'Conferma eliminazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Seller Dashboard
const SellerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <SellerFormProvider>
      <div className="min-h-screen bg-white">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        
        {/* Email Verification Banner */}
        <div className="lg:ml-72">
          <EmailVerificationBanner user={user} />
        </div>
        
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 glass-header px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 rounded-xl hover:bg-[#F5F5F7] transition-colors">
              <Menu className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
            </button>
            <span className="text-lg font-semibold text-[#1D1D1F] tracking-tight">WEQASA</span>
            <div className="w-10" />
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:ml-72 p-6 lg:p-10">
          <Routes>
            <Route index element={<SellerOverview />} />
            <Route path="immobili" element={<MyPropertiesPage />} />
            <Route path="immobili/:propertyId" element={<PropertyDetailPage />} />
            <Route path="carica" element={<UploadPropertyPage />} />
            <Route path="nuovo-immobile" element={<UploadPropertyPage />} />
            <Route path="gare" element={<SellerGarePage />} />
            <Route path="proposte" element={<ProposalsPage />} />
            <Route path="mandati" element={<MandatesPage />} />
            <Route path="messaggi" element={<SellerMessagesPage />} />
            <Route path="notifiche" element={<NotificationsPage />} />
            <Route path="profilo" element={<SellerProfilePage />} />
            <Route path="sicurezza" element={<SellerSecurityPage />} />
            <Route path="privacy" element={<SellerPrivacyDashboardPage />} />
          </Routes>
        </div>
      </div>
    </SellerFormProvider>
  );
};

export default SellerDashboard;
